/*
 * Copyright (c) Crecy Poitiers Agincourt Solutions.
 * See LICENSE in the project root for license information.
 */

let _environment = null;
let _tenantId = null;
let _clientId = null;
let _redirectUri = null;
let _scope = null;

let _testInProgress = false;

let _siteRelativeUrl = null;

let _serverUrl = null;

let _serverRelativeUrlToFolder = null;

let _worksheet = null;

let _cells = null;

let _testCaseRange = null;

let _testEnvironmentURL = null;

let _xsltMap = new Map();

const _testEnvironmentMap = new Map();

let _namedItems = new Map();

//
// SharePoint worker to write request / response payloads back to SharePoint
//
const _writePayloadWorker = new Worker("sharepointWorker.js");
_writePayloadWorker.onmessage = function (e) {
  traceLog(e.data.method, e.data.message);
};

/**
 *
 * @param {*} error
 */
const ErrorHandler = (error) => {
  errorLog(error);
  ToggleSpinner("#run-spinner", false);
};

//
// Test Case worker to execute test cases in parallel
//
const _testCaseWorker = new Worker("testCaseRunnerWorker.js");
_testCaseWorker.onmessage = async function (e) {
  switch (e.data.type) {
    case "traceLog":
      traceLog(e.data.method, e.data.message);
      break;
    case "errorLog":
      ErrorHandler(e.data.error);
      break;
    case "testCaseResponse":
      traceLog(`testCaseWorker.onmessage`, `Received message type "${e.data.type}"`);
      let testCaseResponse = TestCaseResponse.Deserialize(e.data.responseData);
      ProcessTestCaseResponse(testCaseResponse);
      break;
    case "writeResponse":
      traceLog(`testCaseWorker.onmessage`, `Received message type "${e.data.type}"`);
      WriteResponsePayloadToSharePoint(e.data.testCaseResponse, e.data.file);
      break;
    default:
      errorLog(`Unknown event type from testCaseWorker`);
  }
};
//
//
//
_testCaseWorker.onerror = (error) => {
  errorLog(error);
};

const REQUEST = "Request";
const RESPONSE = "Response";

//
// Default fallback transformations
//
let _transformationMap = new Map();
_transformationMap.set(REQUEST, `transformer/generate-request.xslt`);
_transformationMap.set(RESPONSE, `transformer/generate-response-expected-csv.xslt`);

const TEST_HISTORY = "TR4E History";

const TEST_HISTORY_HEADER = [
  "Results Worksheet", // (A)
  "Run Date", // (B)
  "Start Datetime", // (C)
  "End Datetime", // (D)
  "Elapsed", // (E)
  "Name", // (F)
  "Email", // (G)
  "TR4E Subscription", // (H)
  "Environment Name", // (I)
  "Environment URL", // (J)
  "First Test Case", // (K)
  "Last Test Case", // (L)
  "Batch Size", // (M)
  "Stagger Delay", // (N)
  "Simulation", // (O)
  "Sequential Execution", // (P)
  "Debug", // (Q)
  "Results in Separate Table", // (R)
  "Write Base Request", // (S)
  "Write Request", // (T)
  "Write Response", // (U)
];

/**
 *
 * @returns
 */
const IsTr4eSubscriptionEnabled = () => {
  return $("#subscriptionRadioBasic").is(":checked");
};

/**
 *
 * @param {*} regex
 * @param {*} selector
 * @returns
 */
const IsTr4eSubscriptionValid = (regex, selector) => {
  const subscriptionEnabled = IsTr4eSubscriptionEnabled();
  const subscriptionDisabled = $("#subscriptionRadioOff").is(":checked");
  const subscriptionInput = $(selector).val();
  var match = new RegExp(regex, "g");
  return (subscriptionEnabled && match.test(subscriptionInput)) || subscriptionDisabled;
};

/**
 *
 * @returns
 */
const IsTr4eSubscriptionKeyValid = () => {
  return IsTr4eSubscriptionValid(TestCaseConfig.sValidSubscriptionKeyRegex, "#subscriptionKey");
};

/**
 *
 * @returns
 */
const IsTr4eSubscriptionNameValid = () => {
  return IsTr4eSubscriptionValid(TestCaseConfig.sValidSubscriptionNameRegex, "#subscriptionName");
};

/**
 *
 * @param {*} inputId
 * @param {*} feedbackClass
 */
const ButtonToggle = (inputId, originalClass, feedbackClass) => {
  $(inputId).toggleClass(originalClass, false);
  $(inputId).toggleClass(feedbackClass, true);

  setTimeout(() => {
    $(inputId).toggleClass(feedbackClass, false);
    $(inputId).toggleClass(originalClass, true);
  }, 500);
};

/**
 * Test the TR4E subscription using a simple ping. Depending upon the outcome the error messages will be toggled
 */
const PingTr4e = async () => {
  const originalClass = "btn btn-light btn-sm";
  const successClass = "btn btn-success btn-sm";
  const warnClass = "btn btn-danger btn-sm";
  const subscriptionName = $("#subscriptionName").val();
  const subscriptionKey = $("#subscriptionKey").val();
  const pingUrl = `https://api.crecy.com.au/${subscriptionName}/tr4e/v1/ping`;
  let tr4eSubscriptionValid = true;

  try {
    const validKey = IsTr4eSubscriptionKeyValid();
    const validName = IsTr4eSubscriptionNameValid();
    const canTest = validKey && validName;
    if (canTest) {
      const tr4eApiAccessToken = await GetAccessToken("sso", _clientId, _redirectUri, _scope, true);
      const headers = {
        Authorization: tr4eApiAccessToken,
        "Ocp-Apim-Subscription-Key": subscriptionKey,
        "Subscription-Name": subscriptionName,
      };
      try {
        const response = await fetch(pingUrl, { headers: headers });
        if (!response.ok) {
          let message = `Got ${response.status} "${response.statusText}" for "${subscriptionName}"`;
          if (response.status == 403 || response.status == 429) {
            const retryAfter = response.headers.get("retry-after");
            const retryMessage = `retry after ${Intl.NumberFormat().format(retryAfter)} seconds`;
            message =
              retryAfter !== null
                ? `E2000 Subscription "${subscriptionName}" quota exceeded, ${retryMessage}`
                : message;
          }
          errorLog(message);
          tr4eSubscriptionValid = false;
        } else {
          traceLog("PingTr4e", `Valid "${subscriptionName}" with key "${subscriptionKey.substring(0, 4)}.."`, true);
        }
      } catch (error) {
        errorLog(
          `Got ${error.message} for "${subscriptionName}" subscription with key ${subscriptionKey.substring(0, 4)}...`
        );
        tr4eSubscriptionValid = false;
      }
    } else {
      ToggleSubscriptionKeyErrorMessage();
      tr4eSubscriptionValid = false;
    }
  } catch (error) {
    errorLog(`Got ${error.message} for "${subscriptionName}" with key ${subscriptionKey.substring(0, 4)}...`);
    tr4eSubscriptionValid = false;
  } finally {
    ToggleSubscriptionKeyErrorMessage();
    ButtonToggle("#subscriptionTest", originalClass, tr4eSubscriptionValid ? successClass : warnClass);
    return tr4eSubscriptionValid;
  }
}; // PingTr4e

var db = new Dexie("au.com.crecy.TR4EDB");

//
// Define database with two tables: 1. tr4eConfig; 2. tr4eFileStore
//
function InitializeDatabase() {
  db.version(1).stores({
    tr4eConfig: "++id, date",
    tr4eFileStore: "++id, date, [server+folder]", //serverUrl, serverRelativeUrlToFolder, AccessToken, fileInput, arrayBuffer
    tr4eSubscription: "++id, date, subscriptionKey",
  });

  traceLog(`InitializeDatabase`, `Using Dexie v + ${Dexie.semVer}`);

  //
  // Setup persistemce handlers
  //
  const persistedFields = [
    "input[type=radio][name=securityRadio]",
    "#userPrincipal",
    "#userSecret",
    "#simulation",
    "#parallel",
    "#debug",
    "#resultsSeparateTable",
    "#writeRequest",
    "#writeResponse",
  ];

  //
  // On change of a configuration setting persist to DB
  //
  persistedFields.forEach((value, index, array) => {
    $(value).on("change", function () {
      db.transaction("rw", db.tr4eConfig, () => {
        const saveSettings = {
          date: new Date(),
          securityRadio: $("#securityRadio1").is(":checked")
            ? "#securityRadio1"
            : $("#securityRadio2").is(":checked")
            ? "#securityRadio2"
            : "#securityRadio3",
          userPrincipal: $("#userPrincipal").val(),
          userSecret: btoa($("#userSecret").val()),
          simulation: $("#simulation").is(":checked"),
          parallel: $("#parallel").is(":checked"),
          debug: $("#debug").is(":checked"),
          resultsSeparateTable: $("#resultsSeparateTable").is(":checked"),
          writeBaseRequest: $("#writeBaseRequest").is(":checked"),
          writeRequest: $("#writeRequest").is(":checked"),
          writeResponse: $("#writeResponse").is(":checked"),
        };
        // Get a date object for the current time
        const HOUR = 1000 * 60 * 60;
        const anHourAgo = Date.now() - HOUR;
        db.tr4eConfig
          .where("date")
          .below(anHourAgo)
          .delete()
          .then((deleteCount) => {
            traceLog(`InitializeDatabase`, `deleted ${deleteCount} older than ${anHourAgo}`);
          });
        db.tr4eConfig.add(saveSettings).then(() => {
          traceLog(`InitializeDatabase`, `persisted config`);
        });
      });
    });
  });

  //
  // Retrieve persisted values
  //
  db.open()
    .then(function () {
      return db.tr4eConfig.toArray();
    })
    .then(function (saveSettings) {
      //
      // load the most recent saved configuration
      //
      if (saveSettings.length > 0) {
        saveSettings = saveSettings[saveSettings.length - 1];
        $('input[name="securityRadio"]').each(function () {
          const thisRadioId = $(this).attr("id");
          const enable = saveSettings.securityRadio.replace("#", "") === thisRadioId;
          //traceLog(
          //  `InitializeDatabase`,
          //  `Compare '${saveSettings.securityRadio}' to '${thisRadioId}' enable ${enable.toString()}`
          //);
          $(this).prop("checked", enable);
        });

        $("#userPrincipal").val(saveSettings.userPrincipal);
        $("#userSecret").val(atob(saveSettings.userSecret));
        $("#simulation").prop("checked", saveSettings.simulation);
        $("#parallel").prop("checked", saveSettings.parallel);
        $("#debug").prop("checked", saveSettings.debug);
        $("#resultsSeparateTable").prop("checked", saveSettings.resultsSeparateTable);
        $("#writeBaseRequest").prop("checked", saveSettings.writeBaseRequest);
        $("#writeRequest").prop("checked", saveSettings.writeRequest);
        $("#writeResponse").prop("checked", saveSettings.writeResponse);
        traceLog(`InitializeDatabase`, `Set save properties to ${JSON.stringify(saveSettings)}`);

        //
        // Make sure the visibility is set correctly for the auth type
        //
        SetSecurityVisibility($("input[type=radio][name=securityRadio]:checked").val());
      } else {
        traceLog(`InitializeDatabase`, `db is empty`);
      }
    })
    .catch(function (e) {
      errorLog(e);
    });

  //
  // Subscription fields
  //
  const tr4eSubscriptionFields = ["input[type=radio][name=subscriptionRadio]", "#subscriptionKey", "#subscriptionName"];

  //
  // On change of a TR4E configuration setting persist to DB
  //
  tr4eSubscriptionFields.forEach((value, index, array) => {
    $(value).on("change", function () {
      if (IsTr4eSubscriptionKeyValid() && IsTr4eSubscriptionNameValid()) {
        db.transaction("rw", db.tr4eSubscription, () => {
          const saveSettings = {
            date: new Date(),
            subscriptionRadio: $("#subscriptionRadioOff").is(":checked")
              ? "#subscriptionRadioOff"
              : $("#subscriptionRadioBasic").is(":checked")
              ? "#subscriptionRadioBasic"
              : "#subscriptionRadioEnterprise",
            subscriptionKey: btoa($("#subscriptionKey").val()),
            subscriptionName: $("#subscriptionName").val(),
          };
          // Get a date object for the current time
          const HOUR = 1000 * 60 * 60;
          const anHourAgo = Date.now() - HOUR;
          db.tr4eSubscription
            .where("date")
            .below(anHourAgo)
            .delete()
            .then((deleteCount) => {
              traceLog(`InitializeDatabase`, `db.tr4eSubscription deleted ${deleteCount} older than ${anHourAgo}`);
            });
          db.tr4eSubscription.add(saveSettings).then(() => {
            traceLog(`InitializeDatabase`, `persisted config ${JSON.stringify(saveSettings)}`);
          });
        });
      }
    });
  });

  //
  // Retrieve persisted values
  //
  db.open()
    .then(function () {
      return db.tr4eSubscription.toArray();
    })
    .then(function (saveSettings) {
      //
      // load the most recent saved configuration
      //
      if (saveSettings.length > 0) {
        saveSettings = saveSettings[saveSettings.length - 1];
        $('input[name="subscriptionRadio"]').each(function () {
          const thisRadioId = $(this).attr("id");
          const enable = saveSettings.subscriptionRadio.replace("#", "") === thisRadioId;
          $(this).prop("checked", enable);
        });

        $("#subscriptionKey").val(atob(saveSettings.subscriptionKey));
        $("#subscriptionName").val(saveSettings.subscriptionName);
        traceLog(`InitializeDatabase`, `Set tr4eSubscription properties to ${JSON.stringify(saveSettings)}`);

        //
        // Make sure the visibility is set correctly for the auth type
        //
        const subscriptionRadioValue = $("input[type=radio][name=subscriptionRadio]:checked").val();
        SetSubscriptionKeyEnabled(subscriptionRadioValue);
      } else {
        traceLog(`InitializeDatabase`, `db is empty`);
      }
    })
    .catch(function (e) {
      errorLog(e);
    });
} //InitializeDatabase

/**
 *
 * @param {*} xsltUri
 * @returns
 */
async function GetXslt(xslt) {
  let retVal = "";
  let xslText = "";
  start(`GetXslt`);
  try {
    const xsltUri = _transformationMap.get(xslt);
    if (_transformationMap.has(xslt)) {
      if (!_xsltMap.has(xsltUri)) {
        traceLog(`GetXslt`, `Need to load transformer for ${xsltUri}`);
        const domParser = new DOMParser();
        // Note optional forward slash between the SharePoint tenant and 'sites'
        //const isSharepoint = xsltUri.match(SHAREPOINT_TRANSFORMER_URL_RE);
        if (SHAREPOINT_TRANSFORMER_URL_RE.test(xsltUri) ) {
          const sharepointUrl = xsltUri.replace(SHAREPOINT_TRANSFORMER_URL_RE, "$1");
          const folder = xsltUri.replace(SHAREPOINT_TRANSFORMER_URL_RE, "$2");
          const file = xsltUri.replace(SHAREPOINT_TRANSFORMER_URL_RE, "$4");
          const localAccessToken = await GetAccessToken();
          if (localAccessToken == null) throw new Error(`Got null access token attempting to retrieve ${xslt}`);
          traceLog(`GetXslt`, `URL: ${sharepointUrl}\nFOLDER: ${folder}\nFILE: ${file}\nJWT: ${localAccessToken}`);
          xslText = await GetFile(await GetSharepointSite(), folder, file, localAccessToken);
          traceLog(`GetXslt`, `Got ${xslText.length} for ${file} commencing ${xslText.substring(0, 100)}...`);
        } else {
          const xslResponse = await fetch(xsltUri, {
            headers: {
              "Content-Type": "application/xml",
            },
          });
          if (xslResponse.ok) {
            xslText = await xslResponse.text();
          } else {
            throw new Error(`XSLT ${xsltUri} not found`);
          }
        } // source from sharepoint or local
        const xslStylesheet = domParser.parseFromString(xslText, "application/xml");
        _xsltMap.set(xsltUri, xslStylesheet);
        traceLog(`GetXslt`, `Added ${xsltUri}`);
      }
      retVal = _xsltMap.get(xsltUri);
      return retVal;
    } else {
      throw Error(`Missing ${xslt} XSLT`);
    }
  } finally {
    end(`GetXslt`);
  }
}

const ENVIRONMENT_TEMPLATE =
  '<div class="form-check"><input class="form-check-input" type="radio" name="environmentRadio" id="environmentRadio' +
  '%NAME%" value="%VALUE%" url="%VALUE%"  %CHECKED%><label class="form-check-label" for="environmentRadio%NAME%">%NAME%</label> </div>';

const TRANSFORMER_TEMPLATE = '<option value="%VALUE%">%NAME%</option>';

/**
 *
 * @returns
 */
async function GetSharepointSite() {
  if (!_serverUrl || !_siteRelativeUrl) {
    await LoadSharePointConfig();
  }

  return _serverUrl + _siteRelativeUrl;
}

/**
 *
 */
async function GetTestCaseFolder(testCaseFolder) {
  if (!_serverRelativeUrlToFolder) {
    await LoadSharePointConfig();
  }
  return _serverRelativeUrlToFolder + "/" + testCaseFolder;
}

/**
 * Returns the scope for the current test environment
 */
function GetCurrentSelectedEnvironmentScope() {
  return _testEnvironmentMap.get(_testEnvironmentURL).scopes;
}

/**
 * Convenience function to swicth the environment
 */
let SwitchEnvironment = function () {
  _testEnvironmentURL = $("input[type='radio'][name='environmentRadio']:checked").val();
  const scope = _testEnvironmentMap.get(_testEnvironmentURL).scopes;
  traceLog(`SwitchEnvironment`, `Environment switched to ${_testEnvironmentURL} with SCOPE ${scope}`);
};

const CHECK = '<span class="check-green">&check;</span>';

/**
 * Used to test SSO connectivity - if the TR4E subsription is being used the SSO test is against the api.crecy.com.au scope
 */
async function TestSso() {
  start(`TestSso`);
  ToggleSpinner("#test-spinner");
  const scope =
    IsTr4eSubscriptionKeyValid() && IsTr4eSubscriptionNameValid()
      ? _scope
      : _testEnvironmentMap.get(_testEnvironmentURL).scopes;
  try {
    traceLog(`TestSso`, `Testing ${scope} for ${_testEnvironmentURL}`);
    const authResult = await SignIn(_clientId, _redirectUri, [scope]);
    const environmentLabel = $("input[type='radio'][name='environmentRadio']:checked").next(".form-check-label").html();
    const regex = /<span class="check-green">/g;
    const found = environmentLabel.match(regex);
    let environmentTick = null;
    if (!found) {
      environmentTick = `${environmentLabel} ${CHECK}`;
      $("input[type='radio'][name='environmentRadio']:checked").next(".form-check-label").html(environmentTick);
    } else {
      environmentTick = environmentLabel;
    }
    const regexp = /([^✓]{1,})/g;
    traceLog(`TestSso`, `${environmentTick.replace(regexp, "$1")}`, true);
  } catch (error) {
    errorLog(error);
  } finally {
    ToggleSpinner("#test-spinner", false);
    end(`TestSso`);
  }
}

const TR4E_TEST_ENVIRONMENT = "TR4E_TEST_ENVIRONMENT";
const GET_FILES_QUERY = "?$select=Name,ServerRelativeUrl,Length,TimeLastModified&orderBy=Name asc";

const TRANSFORMER_REQUEST_RE = /([Rr][Ee][Qq][Uu]?[Ee]?[Ss]?[Tt]?)/;
const TRANSFORMER_RESPONSE_RE = /([Rr][Ee][Ss][Pp]?[Oo]?[Nn]?[Ss]?[Ee]?)/;

let _TransformerCallbackRegistered = false;

/**
 * Change the request or response transformer. If the transformer name contains 'json' or 'xml' then set the mime type radio
 *
 * @param {*} key either Request or Response
 * @param {*} value the URI e.g. "/sites/TR4E/Shared%20Documents/TR4E/Production/generate-request.xslt"
 */
const SwitchTransformer = (key, value) => {
  _transformationMap.set(key, `${_serverUrl}${value}`);
  traceLog(`SwitchTransformer`, `Set ${key} to ${_transformationMap.get(key)}`);
  const jsonRE = /(json|Json|JSON)/g;
  const xmlRE = /(xml|Xml|XML)/g;
  if (jsonRE.test(value)) {
    traceLog(`SwitchTransformer`, `selecting JSON based upon "${value}"`);
    $("#formatRadioJson").trigger("click");
  } else if (xmlRE.test(value)) {
    traceLog(`SwitchTransformer`, `selecting XML based upon "${value}"`);
    $("#formatRadioXml").trigger("click");
  }
}; // SwitchTransformer

/**
 * Loads the SharePoint folder location using TR4E_TRANSFORMER_LOCATION range
 * Retrieves the list of files from that SharePoint folder location
 * Sets the request / response transformers on the select
 */
async function LoadSharePointTransformersFolder() {
  start(`LoadSharePointTransformersFolder`);
  try {
    const path = await LoadSharePointFolder(TR4E_TRANSFORMER_LOCATION, "#sharePointTransformer");

    const localAccessToken = await GetAccessToken();
    const site = await GetSharepointSite();
    const files = await ListFiles(site, path, localAccessToken, GET_FILES_QUERY);

    let requestIndex = 0;
    let responseIndex = 0;

    //
    // Iterate over the returned matching files
    // URI Encode the ServerRelativeUrl
    //
    files.d.results.forEach((result) => {
      const ServerRelativeUrl = encodeURI(result.ServerRelativeUrl);
      const Name = result.Name;
      const Length = result.Length;
      const TimeLastModified = result.TimeLastModified;

      traceLog(
        `LoadSharePointTransformersFolder`,
        `NAME : ${Name}\nLENGTH: ${Length}\nURL: ${ServerRelativeUrl}\nLAST MODIFIED: ${TimeLastModified}`
      );

      /**
       * Adds the option value to the respective selector, and sets as selected
       * If selected it will also set the transformer to default (in case the user doesn't make a change)
       *
       * @param {*} direction  either Request or Response
       * @param {*} selected either "selected" or ""
       */
      const optionAppender = (direction, selected) => {
        const optionValue = `<option value=${ServerRelativeUrl} ${selected}>${Name}</option>`;
        const selector = `#${direction}Transformer option:last`;

        traceLog(`optionAppender`, `Added ${optionValue} to ${selector}`);
        $(selector).after(optionValue);

        if (selected === "selected") {
          SwitchTransformer(direction, ServerRelativeUrl);
        }
      };

      //
      // If the file name suggests that it is a request transformer just add to the request select
      // If the file name suggests that it is a response transformer just add to the response select
      // Otherwise add to both.
      // The first matching file will be set as the selected one
      //
      if (Name.match(TRANSFORMER_REQUEST_RE)) {
        requestIndex++;
        const selected = requestIndex == 1 ? "selected" : "";
        optionAppender(REQUEST, selected);
      } else if (Name.match(TRANSFORMER_RESPONSE_RE)) {
        responseIndex++;
        const selected = responseIndex == 1 ? "selected" : "";
        optionAppender(RESPONSE, selected);
      } else {
        requestIndex++;
        responseIndex++;
        const selectedRequest = requestIndex == 1 ? "selected" : "";
        const selectedResponse = responseIndex == 1 ? "selected" : "";
        optionAppender(REQUEST, selectedRequest);
        optionAppender(RESPONSE, selectedResponse);
      }
    });

    //
    // Only set up the callbacks on initialization
    //
    if (!_TransformerCallbackRegistered) {
      _TransformerCallbackRegistered = true;
      //
      // Add handler when the request and response transformers are selected
      //
      $("#RequestTransformer").on("change", function () {
        SwitchTransformer(REQUEST, this.value);
      });
      $("#ResponseTransformer").on("change", function () {
        SwitchTransformer(RESPONSE, this.value);
      });

      $("input[type=radio][name=formatRadio]").on("change", function () {
        const targetPayloadFormat = $("input[type=radio][name=formatRadio]:checked").val();
        traceLog("LoadSharePointTransformersFolder", `Target format changed to ${targetPayloadFormat}`);
      });

      //
      // Add a handler to reset the transformation
      // First clean out the existing values
      // Disable the button to prevent multiple submission
      //
      $("#reset-transformations").on("click", async function () {
        try {
          $("#reset-transformations").attr("disabled", "disabled");
          $("#RequestTransformer option").remove();
          $("#RequestTransformer").append("<option selected>Select Request Transformer ...</option>");
          $("#ResponseTransformer option").remove();
          $("#ResponseTransformer").append("<option selected>Select Response Transformer ...</option>");
          await LoadSharePointTransformersFolder();
          traceLog(`LoadSharePointTransformersFolder`, `Reloaded Transformations`, true);
        } catch (error) {
          throw error;
        } finally {
          $("#reset-transformations").removeAttr("disabled");
        }
      });
    }
  } catch (error) {
    traceLog(`LoadSharePointTransformersFolder`, error);
    errorLog(error);
    throw error;
  } finally {
    end(`LoadSharePointTransformersFolder`);
  }
} //LoadSharePointTransformersFolder

/**
 * Load environment detail
 */
async function LoadEnvironments() {
  const themes = GetExcelTableThemes();
  await Excel.run(async (context) => {
    let sheet = context.workbook.worksheets.getItem(TR4E_CONFIG_WORKSHEET);
    if (sheet == null) throw new Error(`E1000 : No 'TR4E' configuration worksheet`);
    if (!_namedItems.has(TR4E_TEST_ENVIRONMENT)) throw new Error(`E1910 : Missing ${TR4E_TEST_ENVIRONMENT}`);
    let range = sheet.getRange(TR4E_TEST_ENVIRONMENT);
    range.load("values");
    await context.sync();
    let env = "";
    for (var row = 0; row < range.values.length; row++) {
      //
      // Use env URL as key and scope as value
      // KEY : https://dctdev.hollard.com.au/DevRetail/dctserver.aspx	 VALUE : api://CAH-Backends-DEV/.default
      //
      const name = range.values[row][0];
      const url = range.values[row][1];
      const scope = range.values[row][2];
      const header = range.values[row][3];
      const headers = header.split(";"); // HRTT-92 multple header values
      const tableStyle = themes[row].tableTheme.tableStyle;
      const worksheetTabColor = themes[row].tabColor;

      const testEnvironment = new TestEnvironment(name, url, scope, headers, tableStyle, worksheetTabColor);
      _testEnvironmentMap.set(url, testEnvironment);

      //
      // Add
      //
      env += ENVIRONMENT_TEMPLATE.replaceAll(
        "%NAME%",
        `<span style='color:${worksheetTabColor}; font-weight:bold'>▮</span> ${name}`
      )
        .replaceAll("%VALUE%", range.values[row][1])
        .replaceAll("%CHECKED%", row == 0 ? "checked" : "");

      traceLog(`LoadEnvironments`, `Added [${row}] ${testEnvironment.ToString()}`);
    }

    $("#testEnvironment").html(env);
    SwitchEnvironment();

    //
    // Add a callback to the radio buttons on change
    //
    $("input[type='radio'][name='environmentRadio']").change(function () {
      SwitchEnvironment();
    });

    //
    // Add handler to test the environment connectivity
    //
    $("#test-environment").on("click", async function (e) {
      e.preventDefault();
      await TestSso();
    });

    //
    // Add a handler to reset the environments
    //
    $("#reset-environment").on("click", async function () {
      await LoadEnvironments();
      traceLog(`LoadEnvironments`, `Reloaded Environments`, true);
    });
  });
}

let _simulationMap = new Map();

const TR4E_SIMULATION_LOCATION = "TR4E_SIMULATION_LOCATION";

/**
 * Load simulation
 */
async function LoadSharePointSimulationFolder() {
  start(`LoadSharePointSimulationFolder`);
  try {
    const path = await LoadSharePointFolder(TR4E_SIMULATION_LOCATION, "#sharePointSimulation");
    if ($("#simulation").is(":checked")) {
      const localAccessToken = await GetAccessToken();
      const site = await GetSharepointSite();
      const files = await ListFiles(site, path, localAccessToken, GET_FILES_QUERY);
      let testCaseIndex = 1;
      //for (row = 0; row < range.rowCount; row++) {
      //  _simulationMap.set(range.values[row][0], range.values[row][1]);
      //}

      //
      // Iterate over the returned matching files
      // URI Encode the ServerRelativeUrl
      //
      files.d.results.forEach((result) => {
        const ServerRelativeUrl = encodeURI(result.ServerRelativeUrl);
        const Name = result.Name;
        const Length = result.Length;
        const TimeLastModified = result.TimeLastModified;

        traceLog(
          `LoadSharePointSimulationFolder`,
          `[${testCaseIndex}] NAME : ${Name}\nLENGTH: ${Length}\nURL: ${ServerRelativeUrl}\nLAST MODIFIED: ${TimeLastModified}`
        );

        _simulationMap.set(testCaseIndex++, ServerRelativeUrl);
      });
    } else {
      traceLog(`LoadSharePointSimulationFolder`, `skipping load, clearing simulation map size ${_simulationMap.size}`);
      _simulationMap.clear();
    }
  } catch (error) {
    traceLog(`LoadSharePointSimulationFolder`, error);
    $(`#simulation`).parent().hide();
  } finally {
    end(`LoadSharePointSimulationFolder`);
  }
} //LoadSharePointSimulationFolder

const SHAREPOINT_TRANSFORMER_URL_RE =
  /(https:\/{2}[^"!@#$^&*\?\\|]{1,}.sharepoint.com\/\/?sites\/\w{1,}\/)([^"!@#$^&*\?\\|]+)(\/)([^"!@#$^&*\?\\|]+\.xslt)$/;
const SHAREPOINT_SERVER_RE = /^(https:\/{2}[^"*:<>?\\|]{1,}.sharepoint.com\/?)$/;
const SHAREPOINT_SITE_RE = /^(\/?sites\/)(\w{1,})(\/?)$/;
const SHAREPOINT_FOLDER_RE = /^(\/?Shared Documents\/)([^!@#$%^&*\?\\|]+)$/;
const TR4E_TEST_CONFIG = "TR4E_TEST_CONFIG";
const TR4E_SHAREPOINT_CONFIG = "TR4E_SHAREPOINT_CONFIG";
const TR4E_TRANSFORMATION = "TR4E_TRANSFORMATION";
const TR4E_TRANSFORMER_LOCATION = "TR4E_TRANSFORMER_LOCATION";
const TR4E_RESULTS_LOCATION = "TR4E_RESULTS_LOCATION";
const TR4E_CONFIG_WORKSHEET = "TR4E";

/**
 *
 */
async function LoadNamedItems() {
  start(`LoadNamedItems`);
  let currentNamedItemName = null;
  try {
    await Excel.run(async (context) => {
      // Log all the named items in the active worksheet.
      let sheet = context.workbook.worksheets.getItem(TR4E_CONFIG_WORKSHEET);
      if (sheet == null) throw new Error(`E1000 : "${TR4E_CONFIG_WORKSHEET}" worksheet missing`);
      const worksheetNamedItems = sheet.names.load();
      const workbookNamedItems = context.workbook.names.load();
      const namedItems = [workbookNamedItems, worksheetNamedItems];
      await context.sync();

      for (let j = 0; j < namedItems.length; j++) {
        const scopedNamedItems = namedItems[j];
        for (let i = 0; i < scopedNamedItems.items.length; i++) {
          const currentNamedItem = scopedNamedItems.items[i];
          currentNamedItem.load("name");
          currentNamedItem.load("comment");
          currentNamedItem.load("valueAsJson");
          await context.sync();
          currentNamedItemName = currentNamedItem.name;
          let jsonValue = currentNamedItem.valueAsJson;
          if (jsonValue.type == "Error") {
            switch (currentNamedItemName) {
              case TR4E_SHAREPOINT_CONFIG:
                throw new Error(`E1020 : Invalid ${TR4E_SHAREPOINT_CONFIG} config`);
              case TR4E_TRANSFORMER_LOCATION:
                throw new Error(`E1040 : Invalid ${TR4E_TRANSFORMER_LOCATION} config`);
              case TR4E_TEST_CONFIG:
                throw new Error(`E1050 : Invalid ${TR4E_TEST_CONFIG} config`);
              default:
                traceLog(
                  `LoadNamedItems`,
                  `Got error for "${currentNamedItem.scope}" named item "${currentNamedItem.name}"`
                );
            }
          } else {
            _namedItems.set(currentNamedItem.name, currentNamedItem.comment);
            traceLog(
              `LoadNamedItems`,
              `Found ${currentNamedItem.scope} named item ${currentNamedItem.name} value ${jsonValue}`
            );
          }
        }
      }
    });
  } catch (error) {
    errorLog(error);
    if (!currentNamedItemName) {
      throw new Error(`E1000 : No 'TR4E' configuration worksheet`);
    } else {
      throw error;
    }
  } finally {
    end(`LoadNamedItems`);
  }
}

/**
 *
 * @param {string[]} inputs An array of inputs to set in error
 */
const OpenSharePointConfigAccordion = (inputs) => {
  const accordion = { button: "#headingSixButton", body: "#collapseSix" };
  OpenInvalidAccordion(inputs, accordion);

  const otherAccordions = [
    {
      button: "#headingTwoButton",
      body: "#collapseTwo",
    },
    {
      button: "#headingThreeButton",
      body: "#collapseThree",
    },
    {
      button: "#headingOneButton",
      body: "#collapseOne",
    },
    {
      button: "#headingFourButton",
      body: "#collapseFour",
    },
    {
      button: "#headingZeroButton",
      body: "#collapseZero",
    },
  ];

  CollapseOtherAccordion(otherAccordions);
};

/**
 *
 */
const TestSharePointConfig = async () => {
  const siteUrls = ["sharepointSubscription", "sharePointTeamSite", "sharePointUrl"];
  const folders = ["sharePointResults", "sharePointTransformer", "sharePointSimulation"];
  const localAccessToken = await GetAccessToken();
  const sharePointSite = await GetSharepointSite();
  let hasError = false;

  if (!sharePointSite) {
    errorLog("Invalid SharePoint site");
    return;
  }

  // Clear existing annotations
  siteUrls.forEach((url) => {
    $(`#${url}`).removeClass("is-invalid");
    $(`#${url}`).removeClass("is-valid");
  });
  folders.forEach((folder) => {
    $(`#${folder}`).removeClass("is-invalid");
    $(`#${folder}`).removeClass("is-valid");
  });

  try {
    const site = await GetSite(sharePointSite, localAccessToken);
    traceLog("TestSharePointConfig", `${JSON.stringify(site)}`);
    siteUrls.forEach((url) => {
      $(`#${url}`).removeClass("is-invalid");
      $(`#${url}`).addClass("is-valid");
    });
  } catch (error) {
    hasError = true;
    siteUrls.forEach((url) => {
      $(`#${url}`).removeClass("is-valid");
      $(`#${url}`).addClass("is-invalid");
    });
  }

  folders.forEach(async (folder) => {
    try {
      const response = await GetFolder(sharePointSite, $(`#${folder}`).val(), localAccessToken);
      traceLog("TestSharePointConfig", `"${folder}" ${JSON.stringify(response)}`);
      $(`#${folder}`).removeClass("is-invalid");
      $(`#${folder}`).addClass("is-valid");
    } catch (error) {
      hasError = true;
      $(`#${folder}`).removeClass("is-valid");
      $(`#${folder}`).addClass("is-invalid");
    }
  });
  const successClass = "btn btn-success btn-sm";
  const warnClass = "btn btn-danger btn-sm";
  const originalClass = "btn btn-light btn-sm";
  let replaceClass = successClass;

  if (!hasError) {
    const sharePointUrl = $("#sharePointUrl").val();
    traceLog("TestSharePointConfig", `Valid SharePoint config for "${sharePointUrl}"`, true);
  } else {
    errorLog(`SharePoint config errors for "${$("sharePointUrl").val()}"`);
    replaceClass = warnClass;
  }
  ButtonToggle("#validate-sharepoint", originalClass, replaceClass);
}; // TestSharePointConfig

/**
 * Handler when one of the SharePoint URL or Site fields change
 */
const RegisterFocusOutHandler = () => {
  const sharePointConfig = [
    { input: "#sharepointSubscription", re: SHAREPOINT_SERVER_RE, position: 0 },
    { input: "#sharePointTeamSite", re: SHAREPOINT_SITE_RE, position: 1 },
  ];

  sharePointConfig.forEach((item) => {
    $(item.input).focusout(async function () {
      const inputVal = $(this).val();
      if (!inputVal || !item.re.test(inputVal)) {
        OpenSharePointConfigAccordion([item.input, "#sharePointUrl"]);
      } else {
        //
        // Remove errors
        //
        $(item.input).removeClass("is-invalid");
        $(item.input).addClass("is-valid");

        //
        // Update the r/o URL
        //
        $("#sharePointUrl").val(`${$("#sharepointSubscription").val()}${$("#sharePointTeamSite").val()}`);
        //
        // Update the TR4E config
        //
        await Excel.run(async (context) => {
          let sheet = context.workbook.worksheets.getItem(TR4E_CONFIG_WORKSHEET);

          let range = sheet.getRange(TR4E_SHAREPOINT_CONFIG);
          range.values = [[$("#sharepointSubscription").val()], [$("#sharePointTeamSite").val()]];
          range.format.autofitColumns();
          range.format.font.color = "blue"; // set the ext to blue to distinguish TR4E changes
          await context.sync();
        });
      }
      await LoadSharePointConfig(false);
    });
  });
}; // RegisterFocusOutHandler

/**
 *
 * @param {boolean} registerHandler if true the handlers are registered
 */
async function LoadSharePointConfig(registerHandler = true) {
  start(`LoadSharePointConfig`);
  const inputs = ["#sharepointSubscription", "#sharePointTeamSite", "#sharePointUrl"];
  try {
    $("#validate-sharepoint").on("click", async function () {
      await TestSharePointConfig();
    });

    await Excel.run(async (context) => {
      let sheet = context.workbook.worksheets.getItem(TR4E_CONFIG_WORKSHEET);
      if (sheet == null) throw new Error(`E1000 : No 'TR4E' configuration worksheet`);
      if (!_namedItems.has(TR4E_SHAREPOINT_CONFIG)) {
        //throw new Error(`E1930 : Missing ${TR4E_SHAREPOINT_CONFIG}`);
        const rangeAddress = "H1:H2";
        const range = sheet.getRange(rangeAddress);
        const cell = range.getCell(0, 0);
        range.addressLocal = TR4E_SHAREPOINT_CONFIG;
        await context.sync();
      } else {
        let range = sheet.getRange(TR4E_SHAREPOINT_CONFIG);
        range.load("values");
        await context.sync();
        if (!Array.isArray(range.values) || !range.values.length) {
          // throw new Error(`E1020 : No test results config`);
          OpenSharePointConfigAccordion(inputs);
        } else {
          //
          // reset the error messages
          //
          inputs.forEach((input) => {
            $(input).removeClass("is-invalid");
            //$(input).addClass("is-valid");
          });
          _serverUrl = range.values[0][0];
          $("#sharepointSubscription").val(_serverUrl);

          _siteRelativeUrl = range.values[1][0];
          $("#sharePointTeamSite").val(_siteRelativeUrl);

          $("#sharePointUrl").val(`${_serverUrl}${_siteRelativeUrl}`);

          const errorArray = new Array();

          if (!_serverUrl || !SHAREPOINT_SERVER_RE.test(_serverUrl) /*|| ! serverUrlExists*/) {
            errorArray.push("#sharepointSubscription");
            errorArray.push("#sharePointUrl");
          }

          if (!_siteRelativeUrl || !SHAREPOINT_SITE_RE.test(_siteRelativeUrl) /*|| ! siteUrlExists*/) {
            errorArray.push("#sharePointTeamSite");
            errorArray.push("#sharePointUrl");
          }

          if (SHAREPOINT_SERVER_RE.test(_serverUrl) && SHAREPOINT_SITE_RE.test(_siteRelativeUrl)) {
            $("#sharepointUrl").val(`${_serverUrl}${_siteRelativeUrl}`);
          }

          if (errorArray.length > 0) {
            OpenSharePointConfigAccordion(errorArray);
            //
            // reset the error messages for items *not* in the error array
            //
            inputs.forEach((input) => {
              if (!errorArray.includes(input)) {
                $(input).removeClass("is-invalid");
                //$(input).addClass("is-valid");
              }
            });
          } else {
            //
            // reset the error messages
            //
            inputs.forEach((input) => {
              $(input).removeClass("is-invalid");
              //$(input).addClass("is-valid");
            });

            traceLog(
              `LoadSharePointConfig`,
              `Server URL ${_serverUrl} site relative URL ${_siteRelativeUrl} folder ${_serverRelativeUrlToFolder}`
            );
          }
        }
      }
    });
  } catch (error) {
    errorLog(error);
    throw error;
  } finally {
    registerHandler && RegisterFocusOutHandler();
    end(`LoadSharePointConfig`);
  }
} // LoadSharePointConfig

/**
 *
 * @param {*} namedRange
 * @param {*} input
 * @returns
 */
async function LoadSharePointFolder(namedRange, input) {
  let returnFolder = "";
  const theRange = namedRange;
  const theInputId = input;
  //
  // register change handler to sync
  //
  $(input).focusout(async function () {
    const path = $(this).val();
    await OnSharePointFolderChange(theInputId, path, theRange);
  }).change( async function(){
    const path = $(this).val();
    await OnSharePointFolderChange(theInputId, path, theRange);
	});

  await Excel.run(async (context) => {
    let sheet = context.workbook.worksheets.getItem(TR4E_CONFIG_WORKSHEET);
    if (sheet == null) throw new Error(`E1000 : No 'TR4E' configuration worksheet`);
    if (!_namedItems.has(namedRange)) throw new Error(`E1950 : Missing ${namedRange}`);
    let range = sheet.getRange(namedRange);
    range.load("values");
    await context.sync();
    if (!Array.isArray(range.values) || !range.values.length) throw new Error(`E1010 : No ${namedRange} value`);

    const folder = range.values[0][0];
    $(input).val(folder);
    if (!folder || !SHAREPOINT_FOLDER_RE.test(folder)) {
      OpenSharePointConfigAccordion([input]);
    } else {
      //
      // Unset any errors
      //
      $(input).removeClass("is-invalid");
      //$(input).addClass("is-valid");
    }
    traceLog(`LoadSharePointFolder`, `${namedRange} folder is ${folder}`);
    returnFolder = folder;
  });

  return returnFolder;
} // LoadSharePointFolder

/**
 *
 */
async function LoadSharePointResultsFolder() {
  start(`LoadSharePointResultsFolder`);
  try {
    _serverRelativeUrlToFolder = await LoadSharePointFolder(TR4E_RESULTS_LOCATION, "#sharePointResults");
    traceLog(`LoadSharePointResultsFolder`, `folder ${_serverRelativeUrlToFolder}`);
  } catch (error) {
    errorLog(error);
    throw error;
  } finally {
    end(`LoadSharePointResultsFolder`);
  }
} // LoadSharePointResultsFolder

/**
 *
 * @param {string} input
 * @param {string} path /Shared Documents/*
 * @param {string} namedRange TR4E_*_LOCATION
 */
const OnSharePointFolderChange = async (input, path, namedRange) => {
  if (!path || !SHAREPOINT_FOLDER_RE.test(path)) {
    OpenSharePointConfigAccordion([input]);
  } else {
    //
    // Unset any error messages
    //
    $(input).removeClass("is-invalid");
    $(input).addClass("is-valid");
    //
    // Sync the change back to the TR4E worksheet
    //
    await Excel.run(async (context) => {
      let sheet = context.workbook.worksheets.getItem(TR4E_CONFIG_WORKSHEET);

      let range = sheet.getRange(namedRange);
      range.values = [[path]];
      range.format.autofitColumns();
      range.format.font.color = "blue"; // set the ext to blue to distinguish TR4E changes
      await context.sync();
    });
  }
}; //OnSharePointFolderChange

/**
 *
 * @param {*} url
 * @returns
 */
function isValidWebUrl(url) {
  let regEx =
    /^(https|api)?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/gm;
  return regEx.test(url);
}

/**
 *
 * @param {*} uuid
 * @returns
 */
function isValidUUID(uuid) {
  var uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
}

/**
 * Go through the performance stats to find the server / host that the manifest XML was served from. The valid matches are:
 * - aucomcrecy.z8.web.core.windows.net
 * - tr4e.io
 * - tr4e.crecy.com.au
 * @param {string} redirectUrl - a modified redirect URL that contains the actual server
 * @returns {string} - a modified redirect URL that contains the actual server
 */
const ChangeHostname = (redirectUrl) => {
  let retVal = redirectUrl;
  const resources = performance.getEntriesByType("resource");
  const re = /https:\/\/(aucomcrecy\.z8\.web\.core\.windows\.net|tr4e\.io|tr4e\.crecy\.com\.au)\/manifest\.xml/g;

  const manifestFileResource = resources.find((entry) => re.test(entry.name));

  if (manifestFileResource) {
    const servingHost = manifestFileResource.name.replace(re, "$1");
    retVal = redirectUrl.replace(/(https:\/\/)([^\/]+)(\/.*)/, `$1${servingHost}$3`);
    traceLog(
      `ChangeHostname`,
      `Serving host "${servingHost}" change redirect URL from "${redirectUrl}" to "${retVal}"`
    );
  } else {
    traceLog(`ChangeHostname`, `No manifest file found`);
  }
  return retVal;
}; // GetHostnameFromPerformance

/**
 *
 */
async function LoadAuthConfig() {
  start(`LoadAuthConfig`);
  try {
    const response = await fetch("./manifest.xml");
    if (response.ok) {
      const manifest = await response.text();
      const domParser = new DOMParser();
      const manifestDocument = domParser.parseFromString(manifest, `text/xml`);

      _environment = manifestDocument.evaluate(
        "/d:OfficeApp/default:DisplayName/@DefaultValue",
        manifestDocument,
        nsResolver,
        XPathResult.STRING_TYPE,
        null
      ).stringValue;

      if (!_environment) {
        throw new Error(`Invalid environment ${_environment}`);
      } else {
        traceLog(`LoadAuthConfig`, `Environment: ${_environment}`);
      }

      const tr4eConfigEncoded = manifestDocument.evaluate(
        "/*[name()='OfficeApp']/*[name()='VersionOverrides']/*[name()='Resources']/bt:LongStrings/bt:String[@id='TR4E.Config']/@DefaultValue",
        manifestDocument,
        nsResolver,
        XPathResult.STRING_TYPE,
        null
      ).stringValue;

      const components = tr4eConfigEncoded.split(".");

      if (!components || components.length < 3) {
        throw new Error(`Invalid TR4E config ${tr4eConfigEncoded}`);
      }

      components.forEach((element, index) => {
        console.log(`[${index}] ${atob(element)}`);
        switch (index) {
          case 0:
            _tenantId = atob(element);
            break;
          case 1:
            _clientId = atob(element);
            break;
          case 2:
            _redirectUri = ChangeHostname(atob(element)); // switch the redirect hostname to match the server that the mainifest.xml was served from
            break;
          case 3:
            _scope = atob(element);
        }
      });

      //
      // Validate the Tenant ID, Client ID and Redirect URL
      //

      if (!isValidWebUrl(_redirectUri)) {
        throw new Error(`Invalid redirect ${_redirectUri}`);
      }

      if (_tenantId !== "common" && !isValidUUID(_tenantId)) {
        throw new Error(`Invalid Tenant ID "${_tenantId}"`);
      } else {
        const entraResponse = await fetch(
          `https://login.microsoftonline.com/${_tenantId}/v2.0/.well-known/openid-configuration`
        );
        if (!entraResponse.ok) {
          throw new Error(`Tenant ${_tenantId} returned ${entraResponse.status} ${entraResponse.statusText}`);
        }
      }

      if (!isValidUUID(_clientId)) {
        throw new Error(`Invalid Client ID ${_clientId}`);
      }

      if (!isValidWebUrl(_scope)) {
        throw new Error(`Invalid Scope ${_scope}`);
      }

      traceLog(
        `LoadAuthConfig`,
        `Environment : ${_environment} Tenant ID : ${_tenantId} Client ID : ${_clientId} Redirect URL : "${_redirectUri} TR4E Scope : ${_scope}"`
      );

      //
      // SSO required for SharePoint access
      //
      await SignInSharePoint(_clientId, _redirectUri, _serverUrl);

      //
      // Set user's profile image
      //
      const profileImage = await GetProfileImage(_clientId, _redirectUri);
      if (profileImage !== null) {
        const href = profileImage.image;
        $("svg > defs > pattern > image").attr("href", href);
        $('circle[stroke="blue"]').attr("stroke", "green");
      }

      //
      // Set tenant ID for OAuth
      //
      $("#tenantId").val(_tenantId);
    } else {
      let error = `LoadAuthConfig ${response.status} ${response.statusText}`;
      errorLog(error);
      throw new Error(error);
    }
  } catch (error) {
    errorLog(error);
    throw error;
  } finally {
    end(`LoadAuthConfig`);
  }
}

/**
 *
 */
async function ConfigureTestCases() {
  start(`ConfigureTestCases`);
  try {
    /**
     * Get configuration
     */
    await Excel.run(async (context) => {
      let workbook = context.workbook;
      let sheet = workbook.worksheets.getItem(TR4E_CONFIG_WORKSHEET);
      if (sheet == null) throw new Error(`E1000 : No 'TR4E' configuration worksheet`);
      if (!_namedItems.has(TR4E_TEST_CONFIG)) throw new Error(`E1960 : Missing ${TR4E_TEST_CONFIG}`);
      let range = sheet.getRange(TR4E_TEST_CONFIG);
      range.load("values");
      await context.sync();
      if (!Array.isArray(range.values) || !range.values.length) throw new Error(`E1050 : No test case config`);
      _worksheet = range.values[0][0];
      _cells = range.values[1][0];
      let sourceSheet = context.workbook.worksheets.getItemOrNullObject(_worksheet);
      _testCaseRange = sourceSheet.getRange(_cells);
      _testCaseRange.load("values");
      _testCaseRange.load("rowCount");
      _testCaseRange.load("columnCount");
      await context.sync();
      if (sourceSheet == null || sourceSheet.isNullObject) throw new Error(`E1060 : No Test Case worksheet`);
      if (!Array.isArray(_testCaseRange.values) || !_testCaseRange.values.length)
        throw new Error(`No test cases configured`);
      //
      // Test case range is 1 less than row count because of header row
      //
      SetMinMax(1, _testCaseRange.rowCount - 1);

      traceLog(`ConfigureTestCases`, `worksheet ${_worksheet} cells ${_cells}`);
    });
  } catch (err) {
    traceLog(`ConfigureTestCases`, err);
    errorLog(err);
    throw err;
  } finally {
    end(`ConfigureTestCases`);
  }
}

/**
 *
 * @param {*} prefix
 * @returns
 */
function nsResolver(prefix) {
  switch (prefix) {
    case "xsi":
      return "http://www.w3.org/2001/XMLSchema-instance";
    case "bt":
      return "http://schemas.microsoft.com/office/officeappbasictypes/1.0";
    case "ov":
      return "http://schemas.microsoft.com/office/taskpaneappversionoverrides";
    default:
      return "http://schemas.microsoft.com/office/appforoffice/1.1";
  }
}

/**
 *
 */
async function LoadVersion() {
  start(`LoadVersion`);
  try {
    const response = await fetch("./manifest.xml");
    if (response.ok) {
      const domParser = new DOMParser();
      const manifest = await response.text();
      if ($("#debug").is(":checked")) {
        traceLog(`LoadVersion`, manifest);
      }
      const manifestDocument = domParser.parseFromString(manifest, `text/xml`);
      const elements = [`default:DisplayName/@DefaultValue`, `default:Version/text()`];
      const elementValues = Array(2);
      let index = 0;
      elements.forEach((element) => {
        elementValues[index++] = manifestDocument.evaluate(
          `/default:OfficeApp/${element}`,
          manifestDocument,
          nsResolver,
          XPathResult.STRING_TYPE,
          null
        ).stringValue;
      });
      traceLog(`LoadVersion`, `Loaded ${elementValues[0]} version ${elementValues[1]}`);
      const nameVersion = `${elementValues[0]} : ${elementValues[1]}`;
      const version = `${elementValues[1]}`;

      $("#pluginVersion").html(nameVersion);
      $(`.card-img-top`).attr(`alt`, `${elementValues[0]}`);
      $(`head > title`).html(`${elementValues[0]}`);

      //
      // Set the banner version
      // /svg/g/g/g[@id='shape94-68']/text/text()
      //
      const selector = 'svg > g > g > g[id="shape94-68"] > text';
      $(selector).last().text(version);
      const newVersion = traceLog(`LoadVersion`, `SVG Version : ${$(selector).text()}`);
    } else {
      let error = `LoadVersion ${response.status} ${response.statusText}`;
      errorLog(error);
      throw new Error(error);
    }
  } catch (error) {
    errorLog(error);
  } finally {
    end(`LoadVersion`);
  }
}

/**
 *
 * @param {*} selector
 * @param {*} namedRange
 * @param {*} itemValidator
 * @param {*} numericArray
 */
async function LoadNumericList(selector, namedRange, itemValidator, numericArray) {
  start(`LoadNumericList`);
  try {
    await Excel.run(async (context) => {
      let sheet = context.workbook.worksheets.getItem(TR4E_CONFIG_WORKSHEET);
      if (!_namedItems.has(namedRange)) throw new Error(`E1970 : Missing ${namedRange}`);
      let range = sheet.getRange(`${namedRange}`);
      range.load("values");
      range.load("rowCount");
      range.load("columnCount");
      await context.sync();
      if (Array.isArray(range.values) && range.columnCount == 1 && range.rowCount > 0) {
        //
        // If the batch selector is false then set to disabled
        //
        const parallelExecution = $("#parallel").is(":checked");
        $(selector).prop("disabled", parallelExecution);

        //
        // Toggle on / off when something changes
        //
        $("#parallel").change(function (event) {
          const parallelExecution = $(this).is(":checked");
          $(selector).prop("disabled", parallelExecution);
        });

        //
        // start with a default array - in case there are no valid entries
        //
        const selectValues = [];
        for (i = 0; i < range.rowCount; i++) {
          const numericValue = range.values[i][0];
          if (itemValidator(numericValue)) {
            if (!selectValues.includes(Number.parseInt(numericValue))) {
              traceLog(`LoadBatch`, `Adding ${numericValue}`);
              selectValues.push(Number.parseInt(numericValue));
            } else {
              traceLog(`LoadNumericList`, `Skipping duplicate ${numericValue}`);
            }
          } else {
            traceLog(`Skipping invalid ${numericValue}`);
          }
        }
        //
        // If there are no valid configured values then use defaults; or
        // Having created an array of unique values sort in ascending numeric order and add to the drop down
        //
        if (selectValues.length == 0) {
          numericArray.forEach((element, index) => {
            traceLog("LoadNumericList", `${namedRange} default [${index}] = ${element}`);
            selectValues.push(element);
          });
        } else {
          selectValues.sort(function (a, b) {
            return a - b;
          });
        }

        // Default the selected value to the midpoint
        const midpointIndex = Math.ceil(selectValues.length / 2);
        selectValues.forEach((item, index) => {
          const selected = index == midpointIndex ? " selected" : "";
          const nextOption = `<option value="${item}"${selected}>${item}</option>`;
          $(nextOption).insertAfter($(`${selector} option:last-child`));
        });
        if ($("#debug").is(":checked")) {
          const html = $(selector).html();
          traceLog(`LoadNumericList`, `${html}`);
        }
      } else {
        throw new Error(`E1970 : unexpected row count = ${range.rowCount} or column count = ${range.columnCount}`);
      }
    });
  } catch (error) {
    traceLog(`LoadNumericList`, `Got ${error}`);
  } finally {
    end(`LoadNumericList`);
  }
} // LoadNumericList

/**
 * Loads the batch drop down
 */
async function LoadBatch() {
  /**
   *  Valid batch sizes are valid integers between 50 and 5,000
   * @param {*} value
   * @returns
   */
  const IsValidBatch = (value) => {
    const re = /\d{1,4}/;
    return (
      value !== null &&
      re.test(value) &&
      Number.isInteger(value) &&
      Number.parseInt(value) < 5001 &&
      Number.parseInt(value) > 49
    );
  };

  start(`LoadBatch`);
  const batchArray = [50, 100, 200, 250, 500, 750, 1000];
  await LoadNumericList("#batchSelector", "TR4E_BATCH", IsValidBatch, batchArray);
  end(`LoadBatch`);
} // LoadBatch

/**
 * Loads the delay dropdown
 */
async function LoadDelay() {
  /**
   * Valid stagger delay is between 1ms and 5,000ms
   * @param {*} value
   * @returns
   */
  const IsValidDelay = (value) => {
    const re = /\d{1,4}/;
    return (
      value !== null &&
      re.test(value) &&
      Number.isInteger(value) &&
      Number.parseInt(value) < 5001 &&
      Number.parseInt(value) > 0
    );
  };

  start(`LoadDelay`);
  // Default Fibonacci sequence of delay values from 1ms to 1,000ms
  const delayArray = [1, 2, 3, 5, 8, 13, 21, 34, 55, 89, 144, 233, 377, 610, 987];
  await LoadNumericList("#delaySelector", "TR4E_DELAY", IsValidDelay, delayArray);
  end(`LoadDelay`);
} // LoadDelay

/**
 *
 */
const ToggleSubscriptionKeyErrorMessage = () => {
  if (IsTr4eSubscriptionKeyValid()) {
    $("#subscriptionKey").removeClass("is-invalid");
    $("#subscriptionKey").addClass("is-valid");
  } else {
    $("#subscriptionKey").removeClass("is-valid");
    $("#subscriptionKey").addClass("is-invalid");
  }
  if (IsTr4eSubscriptionNameValid()) {
    $("#subscriptionName").removeClass("is-invalid");
    $("#subscriptionName").addClass("is-valid");
  } else {
    $("#subscriptionName").removeClass("is-valid");
    $("#subscriptionName").addClass("is-invalid");
  }
}; //ToggleSubscriptionKeyErrorMessage

/**
 *
 * @param {*} accordions
 */
const CollapseOtherAccordion = (accordions) => {
  accordions.forEach((accordion) => {
    const button = accordion.button;
    const accordionBody = accordion.body;
    traceLog(`CollapseOtherAccordion`, `BUTTON : "${button}" BODY : '${accordionBody}"`);

    $(accordionBody).removeClass("show");
    $(button).attr("aria-expanded", "false");
    $(button).addClass("collapsed");
  });
};

/**
 *
 * @param {*} input
 * @param {*} accordion
 */
const OpenInvalidAccordion = (input, accordion) => {
  const button = accordion.button;
  const accordionBody = accordion.body;
  traceLog(`OpenInvalidAccordion`, `INPUT : "${input}" BUTTON : "${button}" BODY : "${accordionBody}"`);

  if (Array.isArray(input)) {
    input.forEach((element) => {
      $(element).removeClass("is-valid");
      $(element).addClass("is-invalid");
    });
  } else {
    $(input).removeClass("is-valid");
    $(input).addClass("is-invalid");
  }

  $(accordionBody).addClass("show");
  $(button).attr("aria-expanded", "true");
  $(button).removeClass("collapsed");
};

/**
 * 
 */
const SharePointFolderNavigatorSetup = () => {
  const sharePointFolders = ["#sharePointSimulation", "#sharePointResults", "#sharePointTransformer"];

  const closeSelectors = ["#dismissSharePointPickerModal1", "#dismissSharePointPickerModal2"];

  closeSelectors.forEach((selector) => {
    $(selector).on("click", function () {
      $("#sharePointPickerModal").addClass("d-none");
    });
  });

  sharePointFolders.forEach((selector) => {
    $(selector).on("click", async function () {
      const modalTitle = $(selector).siblings('label').text().replace('URI', 'Selector');
      $("#sharePointPickerModalTitle").text(modalTitle);
      $("#sharePointPickerModal").removeClass("d-none");
      const accessToken = await GetAccessToken();
      await SharePointNavigatorLoadFolders(selector, accessToken, $(this).val());
    });
  });

  
  /**
   * 
   * @param {*} widthAdj 
   * @param {*} heightAdj 
   */
  const ResizeViewBox = (widthAdj, heightAdj) => {
    const viewBoxItems = $(_folderSvgSelector).attr('viewBox').split(' ');
    if (viewBoxItems.length == 4) {
      const xOffset = viewBoxItems[0];  
      const yOffset = viewBoxItems[1];
      const width = Math.floor(Number.parseFloat(viewBoxItems[2]) * widthAdj);
      const height =  Math.floor(Number.parseFloat(viewBoxItems[3]) * heightAdj);
      const viewBox = `${xOffset} ${yOffset} ${width} ${height}`;
      traceLog('ResizeViewBox', viewBox);
      $(_folderSvgSelector).attr('viewBox', viewBox);
    }
  }

  $('#zoomInSharePointPicker').on('click', function () {
     ResizeViewBox(0.9, 0.9);
  });

  $('#zoomOutSharePointPicker').on('click', function () {
     ResizeViewBox(1.1, 1.1);
  });
};

/**
 *
 */
async function ConfigSetup() {
  start(`ConfigSetup`);
  try {
    //
    // Disable panel while loading config
    //
    $(`#panelOverlay`).show();
    $(`html`).addClass(`prevent-scrolling`);

    const browser = BrowserCheck();

    //
    // EDGe HTML and IE are not supported do not continue
    //
    if (browser.isIEBrowser() || browser.isEdgeBrowser()) {
      const errorMsg = `This add-in won't run in your version of Office. Please upgrade either to perpetual Office 2021 (or later) or to a Microsoft 365 account.`;
      traceLog(`Unsupported browser: ${browser.browserName()}`);
      errorLog(errorMsg);
      return;
    }

    //
    // Initialize DB
    //
    InitializeDatabase();
    //
    // Named items need to be loaded first to make sure config is correct
    //
    await LoadNamedItems();
    await LoadVersion();
    await LoadSharePointConfig();
    await LoadAuthConfig();
    await LoadEnvironments();
    await LoadSharePointTransformersFolder();
    await LoadSharePointSimulationFolder();
    await LoadSharePointResultsFolder();
    await LoadBatch();
    await LoadDelay();
    await ConfigureTestCases();
    SharePointFolderNavigatorSetup();
    SliderSetup();
    //
    // Overlay can be hidden when config complete
    //
    $(`#panelOverlay`).hide();
    $(`html`).removeClass(`prevent-scrolling`);

    /**
     *
     * @returns true if the TR4E subscription is valid
     */
    const Tr4eSubscriptionAccordionIsValid = () => {
      let canRun = true;
      if (!IsTr4eSubscriptionKeyValid() || !IsTr4eSubscriptionNameValid()) {
        canRun = false;

        const accordion = { button: "#headingZeroButton", body: "#collapseZero" };

        OpenInvalidAccordion("#subscriptionKey", accordion);

        const otherAccordions = [
          {
            button: "#headingTwoButton",
            body: "#collapseTwo",
          },
          {
            button: "#headingThreeButton",
            body: "#collapseThree",
          },
          {
            button: "#headingOneButton",
            body: "#collapseOne",
          },
          {
            button: "#headingFourButton",
            body: "#collapseFour",
          },
          {
            button: "#headingSixButton",
            body: "#collapseSix",
          },
        ];

        CollapseOtherAccordion(otherAccordions);
      }
      ToggleSubscriptionKeyErrorMessage();
      return canRun;
    }; //ValidateTr4eSubsriptionAccordion

    /**
     *
     * @returns true if the username and password are valid
     */
    const SecurityAccordionIsValid = () => {
      let canRun = true;

      //
      // Only validate user principal and user secret if security is not SSO
      //
      const authType = $("input[type='radio'][name='securityRadio']:checked").val();
      const auth = ["#userPrincipal", "#userSecret"];
      if (authType !== "sso") {
        traceLog(`RegisterSecurityValidation`, `perform validation for ${authType}`);
        for (let i = 0; i < auth.length; i++) {
          //
          // If there is a validation error - then display errors
          //
          if (!$(auth[i]).val()) {
            const accordion = { button: "#headingOneButton", body: "#collapseOne" };

            OpenInvalidAccordion(transformer, accordion);

            const otherAccordions = [
              {
                button: "#headingTwoButton",
                body: "#collapseTwo",
              },
              {
                button: "#headingThreeButton",
                body: "#collapseThree",
              },
              {
                button: "#headingFourButton",
                body: "#collapseFour",
              },
              ,
              {
                button: "#headingSixButton",
                body: "#collapseSix",
              },
              {
                button: "#headingZeroButton",
                body: "#collapseZero",
              },
            ];

            CollapseOtherAccordion(otherAccordions);

            canRun = false;
          } else {
            $(auth[i]).removeClass("is-invalid");
            $(auth[i]).addClass("is-valid");
          }
        }
        $(this).addClass("was-validated");
      } else {
        traceLog(`RegisterSecurityValidation`, `skip validation for ${authType}`);
      }
      return canRun;
    }; // ValidateSecurityAccordion

    /**
     *
     * @returns true if the transformers are valid
     */
    const TransformerAccordionIsValid = () => {
      var canRun = true;
      const regexp = /^Select Re(quest|sponse) Transformer \.{3}$/;
      const transformerSelector = ["#RequestTransformer", "#ResponseTransformer"];
      transformerSelector.forEach((transformer) => {
        if ($(transformer).val().match(regexp)) {
          canRun = false;

          const accordion = { button: "#headingFourButton", body: "#collapseFour" };

          OpenInvalidAccordion(transformer, accordion);

          const otherAccordions = [
            {
              button: "#headingTwoButton",
              body: "#collapseTwo",
            },
            {
              button: "#headingThreeButton",
              body: "#collapseThree",
            },
            {
              button: "#headingOneButton",
              body: "#collapseOne",
            },
            {
              button: "#headingZeroButton",
              body: "#collapseZero",
            },
            ,
            {
              button: "#headingSixButton",
              body: "#collapseSix",
            },
          ];

          CollapseOtherAccordion(otherAccordions);
        } else {
          $(transformer).removeClass("is-invalid");
          $(transformer).addClass("is-valid");
        }
      });
      return canRun;
    }; // ValidateTransformerAccordion

    //
    // Register the "Test TR4E Subscription" button
    //
    $("#subscriptionTest").on("click", async function (e) {
      await PingTr4e();
    });

    //
    // Register "Run" button handler
    //
    $("#load-sheet").on("click", async function (e) {
      e.preventDefault();

      //
      // clear the logging map
      //
      clearLoggingMap();

      //
      // Validate the accordions one at a time and fail fast on errors
      //
      if (SecurityAccordionIsValid()) {
        if (TransformerAccordionIsValid()) {
          if (Tr4eSubscriptionAccordionIsValid()) {
            const startPosition = GetStart() - 1;
            const firstTestCaseIndex = GetStart();
            const endPosition = GetEnd();
            const lastTestCaseIndex = GetEnd();
            const testCaseCount = GetEnd() - GetStart() + 1;
            const serialExecution = $("#parallel").is(":checked");
            await CreateTestCases(
              startPosition,
              firstTestCaseIndex,
              endPosition,
              lastTestCaseIndex,
              testCaseCount,
              serialExecution
            );
          } // Tr4eSubscriptionAccordionIsValid
        } // TransformerAccordionIsValid
      } // SecurityAccordionIsValid
    });
  } catch (error) {
    throw error;
  } finally {
    end(`ConfigSetup`);
  }
} // ConfigSetup

/**
 *
 */
function SetOAuthCredentialsIfRequired() {
  //
  // If using OAuth then set the parameters
  //
  if ($("input[type=radio][name=securityRadio]").val() == "oauth") {
    const tenantId = $(`#tenantId`).val();
    const clientId = $(`#userPrincipal`).val();
    const clientSecret = $(`#userSecret`).val();
    traceLog(
      `Office.initialize`,
      `Reset OAuth information : TENANT ID : ${tenantId} CLIENT ID : ${clientId} CLIENT SECRET : ${clientSecret.substring(
        1,
        5
      )}...`
    );
    SetOAuthCredentials(tenantId, clientId, clientSecret);
  }
}

/**
 *
 * @param {*} authType
 */
function SetSecurityVisibility(authType) {
  traceLog(`SetSecurityVisibility`, `Reset for ${authType}`);
  if (authType == "basic") {
    $(".form-group").prop("hidden", false);
    $("#userPrincipalLabel").html("Username");
    $("#userSecretLabel").html("Password");
    $("#userPrincipal").attr("placeholder", "Enter username");
    $("#userSecret").attr("placeholder", "Enter password");
  } else if (authType == "oauth") {
    $(".form-group").prop("hidden", false);
    $("#userPrincipalLabel").html("Client ID");
    $("#userSecretLabel").html("Client Secret");
    $("#userSecretLabel").html("Client Secret");
    $("#userPrincipal").attr("placeholder", "Enter Client ID");
    $("#userSecret").attr("placeholder", "Enter Client Secret");
  } else if (authType == "sso") {
    $(".form-group").prop("hidden", true);
  }
}

/**
 * If TR4E subscription is disabled, then disable the input fields and test button
 *
 * @param {*} subscriptionType
 */
function SetSubscriptionKeyEnabled(subscriptionType) {
  const subscriptionInput = ["Name", "Key", "Test"];
  subscriptionInput.forEach((suffix) => {
    const selector = `#subscription${suffix}`;
    if (subscriptionType == "off") {
      traceLog(
        `SetSubscriptionKeyEnabled`,
        `Use your TR4E subscription for batch size > 100, and stagger delay < 250ms`,
        true
      );
      $(selector).attr("disabled", "disabled");
    } else {
      $(selector).removeAttr("disabled");
    }
  });
}

/**
 *
 */
function RegisterSecurityValidation() {
  const auth = ["#userPrincipal", "#userSecret"];

  //
  // When the user principal and user secret change toggle validity
  //
  for (let i = 0; i < auth.length; i++) {
    $(auth[i]).on("change", function () {
      if ($(this).val()) {
        $(this).removeClass("is-invalid");
        $(this).addClass("is-valid");
      }
      //
      // If using OAuth then set the parameters
      //
      SetOAuthCredentialsIfRequired();
    });
  }
}

/**
 * THIS IS SUPPRESSED FOR NOW
 * @param {*} event
 */
const ResizeHandler = (event) => {
  // Get the new dimensions
  var newWidth = event.resizedWidth;
  var newHeight = event.resizedHeight;

  const viewBox = $("svg").attr("viewBox").split(" ");
  const vbX = viewBox[0];
  const vbY = viewBox[1];
  const vbWidth = viewBox[2];
  const vbHeight = viewBox[3];
  const width = $("svg").attr("width");
  const height = $("svg").attr("height");

  traceLog(`SVG Width: ${width} Height: ${height} ViewBox [${vbX} ${vbY} ${vbWidth} ${vbHeight}]`);

  $("svg").attr("width", number(newWidth));
};

/**
 *
 * @returns
 */
const BrowserCheck = () => {
  var self = {};

  /// IE 6+
  self.isIEBrowser = function () {
    return navigator.userAgent.indexOf("Trident") !== -1;
    //return /*@cc_on!@*/false || !!document.documentMode;
  };

  /// Opera 8.0+
  self.isOperaBrowser = function () {
    return (!!window.opr && !!opr.addons) || !!window.opera || navigator.userAgent.indexOf(" OPR/") >= 0;
  };

  /// Firefox 1.0+
  self.isFirefoxBrowser = function () {
    return typeof InstallTrigger !== "undefined";
  };

  /// Safari 3.0+
  self.isSafariBrowser = function () {
    return (
      /constructor/i.test(window.HTMLElement) ||
      (function (p) {
        return p.toString() === "[object SafariRemoteNotification]";
      })(!window["safari"] || (typeof safari !== "undefined" && window["safari"].pushNotification))
    );
  };

  /// Edge 20+
  self.isEdgeBrowser = function () {
    const edgeCheckA = navigator.userAgent.indexOf("Edge") !== -1;
    const edgeCheckB = !self.isIEBrowser() && !!window.StyleMedia;
    return edgeCheckA || edgeCheckB;
  };

  /// Chrome 1 - 87
  self.isChromeBrowser = function () {
    return (
      (!!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime)) ||
      (navigator.userAgent.indexOf("Chrome") > -1 && !self.isOperaBrowser())
    );
  };

  /// Edge (based on chromium)
  self.isEdgeChromiumBrowser = function () {
    return self.isChromeBrowser() && navigator.userAgent.indexOf("Edg") != -1;
  };

  /// Blink
  self.isBlinkBasedOnBrowser = function () {
    return (self.isChromeBrowser() || self.isOperaBrowser()) && !!window.CSS;
  };

  /// Returns the name of the navigator
  self.browserName = function () {
    if (self.isOperaBrowser()) return "Opera";

    if (self.isEdgeBrowser()) return "Edge";

    if (self.isEdgeChromiumBrowser()) return "Edge (based on chromium)";

    if (self.isFirefoxBrowser()) return "Firefox";

    if (self.isIEBrowser()) return "Internet Explorer";

    if (self.isSafariBrowser()) return "Safari";

    if (self.isChromeBrowser()) return "Chrome";

    return "Unknown";
  };

  traceLog(`BrowserCheck`, `using ${self.browserName()}`);

  return self;
};

Office.initialize = function (reason) {
  $(document).ready(function () {
    traceLog(`Office.initialize`, `initialized ${reason}`);
    // Check if the Office client is Excel
    if (Office.context.host === Office.HostType.Excel) {
      // Office.context.ui.addHandlerAsync(Office.EventType.TaskpaneResize, ResizeHandler);
    }
  });

  /* global console, document, Excel, Office */
  Office.onReady((info) => {
    if (info.host === Office.HostType.Excel) {
      document.getElementById("app-body").style.display = "flex";

      /**
       * Toggle between basic auth and OAuth 2
       */
      $("input[type=radio][name=securityRadio]").change(function () {
        SetSecurityVisibility(this.value);
      });

      //
      // Enable / disable subscription key
      //
      $("input[type=radio][name=subscriptionRadio]").change(function () {
        SetSubscriptionKeyEnabled(this.value);
      });

      //
      // Setup validation checks
      //
      RegisterSecurityValidation();

      //
      // First Run Experience Modal
      //
      const modalContainer = document.getElementById("freModal");
      const freDone = "au.com.crecy.tr4e.freDone";
      const closeSelectors = ["dismissFREModal2", "dismissFREModal1"];

      closeSelectors.forEach((selector) => {
        document.getElementById(selector).addEventListener("click", () => {
          modalContainer.classList.add("d-none");
        });
      });

      /*
      document.getElementById("dismissFREModal2").addEventListener("click", () => {
        modalContainer.classList.add("d-none");
      });

      document.getElementById("dismissFREModal1").addEventListener("click", () => {
        modalContainer.classList.add("d-none");
      });

      document.getElementById("dismissFREModal2").addEventListener("click", () => {
        modalContainer.classList.add("d-none");
      });
      */

      // showedFRE is created and set to "true" when you call showFirstRunExperience().
      if (!localStorage.getItem(freDone)) {
        modalContainer.classList.remove("d-none");
        localStorage.setItem(freDone, true);
      }

      //
      // End First Run Experience Modal
      //

      ConfigSetup()
        .then(() => {
          traceLog(`Office.onReady`, `configuration complete`);
        })
        .catch((error) => {
          traceLog(`Office.onReady`, `Display error pane`);
          errorLog(error);
        });
    }
  }); //Office.onReady
};

/**
 * Rate Test Tool
 */

/**
 *
 * @param {*} excelTimestamp
 * @returns
 */
function ConvertDateExcel(excelTimestamp) {
  // 1. Subtract number of days between Jan 1, 1900 and Jan 1, 1970, plus 1 (Google "excel leap year bug")
  // 2. Convert to milliseconds.
  const secondsInDay = 24 * 60 * 60;
  const excelEpoch = new Date(1899, 12, 31); //new Date(1900, 1, 1);
  const excelEpochAsUnixTimestamp = excelEpoch.getTime();
  const missingLeapYearDay = secondsInDay * 1000;
  const delta = excelEpochAsUnixTimestamp - missingLeapYearDay;
  const excelTimestampAsUnixTimestamp = excelTimestamp * secondsInDay * 1000;
  const monthMillis = secondsInDay * 30 * 1000;
  const parsed = excelTimestampAsUnixTimestamp + delta - monthMillis;
  return isNaN(parsed) ? null : new Date(parsed).toISOString().substring(0, 10);
} //ConvertDateExcel

/**
 *
 * @param {*} serverUrl
 * @param {*} serverRelativeUrlToFolder
 * @param {*} AccessToken
 * @param {*} fileInput
 * @param {*} arrayBuffer
 */
async function WritePayloadFileAsync(
  serverUrl,
  serverRelativeUrlToFolder,
  AccessToken,
  fileInput,
  arrayBuffer,
  batchMode = false
) {
  start(`WritePayloadFileAsync`);
  try {
    if (batchMode) {
      db.transaction("rw", db.tr4eFileStore, function () {
        //
        // In batch model persist copy to index DB
        //
        db.tr4eFileStore.add({
          date: new Date(),
          server: serverUrl,
          folder: serverRelativeUrlToFolder,
          file: fileInput,
          arrayBuffer: arrayBuffer,
        });
      }).catch(function (e) {
        errorLog(e);
      });
    } else {
      //
      // Non batch use SharePoint webbworker to write file immediately
      //
      const event = {
        serverUrl: serverUrl,
        serverRelativeUrlToFolder: serverRelativeUrlToFolder,
        AccessToken: AccessToken,
        fileInput: fileInput,
        arrayBuffer: arrayBuffer,
      };
      _writePayloadWorker.postMessage(event);
    }
  } catch (error) {
    errorLog(error);
    throw error;
  } finally {
    end(`WritePayloadFileAsync`);
  }
} // WritePayloadFileAsync

/**
 *
 * @param {*} testCaseRequest
 * @returns testCase with sourceXML
 */
async function TransformTestCaseRequest(testCaseRequest) {
  const testCaseIndex = testCaseRequest.ordinalPosition;
  const method = `TransformTestCaseRequest[${testCaseIndex}]`;
  try {
    start(method);
    traceLog(method, `Creating test case ${testCaseIndex}`);
    const resultWorksheetName = testCaseRequest.resultWorksheet;
    const xml = testCaseRequest.sourceXml;
    const domParser = new DOMParser();
    const xsltProcessor = new XSLTProcessor();

    const xslStylesheet = await GetXslt(REQUEST);
    xsltProcessor.importStylesheet(xslStylesheet);

    const url = testCaseRequest.testCaseConfig.url;
    const debug = testCaseRequest.testCaseConfig.debug;
    const userPrincipal = testCaseRequest.testCaseConfig.userPrincipal;
    const userSecret = testCaseRequest.testCaseConfig.userSecret;
    //
    // If basic authentication then add username / password to transform
    //
    if (TestCaseConfig.IsBasicAuth(testCaseRequest.testCaseConfig)) {
      if (TestCaseConfig.HasValidBasicAuthCredentials(testCaseRequest.testCaseConfig)) {
        traceLog(method, `Adding basic auth header/secret`);
        xsltProcessor.setParameter(null, "username", userPrincipal);
        xsltProcessor.setParameter(null, "password", userSecret);
      } else {
        throw new Error(`Invalid "${userPrincipal}" or "${userSecret}"`);
      }
    } else if (TestCaseConfig.HasValidBasicAuthCredentials(testCaseRequest.testCaseConfig)) {
      //
      // HRTT-91 for SSO populate the username / password if they are valid and available
      //
      xsltProcessor.setParameter(null, "username", userPrincipal);
      xsltProcessor.setParameter(null, "password", userSecret);
    }
    xsltProcessor.setParameter(null, "url", url);
    xsltProcessor.setParameter(null, "testCase", testCaseIndex);
    // HRTT-109 optionally pass the debug flag
    if (debug) {
      traceLog(method, "Passing debug = true");
      xsltProcessor.setParameter(null, "debug", true);
    }
    let sourceDocument = domParser.parseFromString(xml, "text/xml");
    const result = xsltProcessor.transformToFragment(sourceDocument, sourceDocument);
    let requestPayload = null;
    if (testCaseRequest.IsXmlTarget()) {
      //
      // The target payload is XML
      //
      if (!result || result?.firstElementChild == null || result?.firstElementChild?.outerHTML == null) {
        throw Error(`Got empty XML request payload for ${testCaseRequest.ordinalPosition}`);
      } else {
        requestPayload = result.firstElementChild.outerHTML;
      }
    } else if (testCaseRequest.IsJsonTarget()) {
      //
      // The target paylaod is JSON
      //
      if (!result || result?.firstChild == null || result?.firstChild?.textContent == null) {
        throw Error(`Got empty JSON request payload for ${testCaseRequest.ordinalPosition}`);
      } else {
        requestPayload = result.firstChild.textContent;
      }
    }

    traceLog(method, `Created test case ${testCaseIndex} size ${requestPayload.length}`);

    /**
     *
     * @param {*} plaintext
     * @returns masked sensitive information - leaving first two charcters in plaintext
     */
    let masked = (plaintext) => {
      const regex = /([\w$&+,:;=?@#|'<>.\-^*()%!])/g;
      return `${plaintext.substring(0, 2)}${plaintext.substring(2).replace(regex, "*")}`;
    };

    /**
     * Replaces plaintext username and password with masked versions. It checks for username and password in single or double quotes
     * @param {*} xmlText
     * @returns the masked value
     */
    let replacer = (xmlText, userPrincipal, userSecret) => {
      return xmlText
        .replaceAll(`"${userPrincipal}"`, `"${masked(userPrincipal)}"`)
        .replaceAll(`"${userSecret}"`, `"${masked(userSecret)}"`)
        .replaceAll(`'${userPrincipal}'`, `'${masked(userPrincipal)}'`)
        .replaceAll(`'${userSecret}'`, `'${masked(userSecret)}'`);
    };

    //
    // Write the test case to Sharepoint
    //
    if (testCaseRequest.testCaseConfig.writeRequest) {
      //
      // redact the username / password from the payload before saving
      //
      const redactedXmlText = TestCaseConfig.HasValidBasicAuthCredentials(testCaseRequest.testCaseConfig)
        ? replacer(requestPayload, userPrincipal, userSecret)
        : requestPayload;
      const localAccessToken = testCaseRequest.accessToken;
      const batchMode = testCaseRequest.IsBatch();
      const isWritingFiles = TestCaseRequest.IsWritingFiles(testCaseRequest);
      //
      // Pass 'true' for batch mode
      //
      await WritePayloadFileAsync(
        await GetSharepointSite(),
        await GetTestCaseFolder(resultWorksheetName),
        localAccessToken,
        `${REQUEST}_${testCaseRequest.ordinalPosition}.${testCaseRequest.targetPayloadFormat}`,
        redactedXmlText,
        batchMode && isWritingFiles
      )
        .then(async () => {
          traceLog(method, `Wrote test case ${testCaseIndex}`);
        })
        .catch(async (message) => {
          errorLog(message);
        });
    }
    testCaseRequest.sourceXml = requestPayload;
    return testCaseRequest;
  } catch (err) {
    errorLog(err);
    throw err;
  } finally {
    end(method);
  }
} //GenerateTestCase

/**
 *
 * @param {*} testCaseResponse
 */
async function ProcessTestCaseResponse(testCaseResponse) {
  const testCaseIndex = testCaseResponse.ordinalPosition;
  const testCaseCount = testCaseResponse.testCaseCount;
  const testCaseCurrentPosition = TestCaseResponse.GetProcessedResponseCount();
  const method = `ProcessTestCaseResponse[${testCaseIndex}]`;
  start(method);
  try {
    if (testCaseResponse.testCaseConfig.debug) {
      traceLog(method, testCaseResponse.ToString());
    }
    traceLog(method, `Transforming test case ${testCaseIndex} of ${testCaseCount}`, true);
    if (testCaseResponse.IncludeHeader()) {
      testCaseResponse = await TransformTestCaseResponse(testCaseResponse);
    }
    traceLog(method, `Transformed test case ${testCaseIndex} : ${testCaseCurrentPosition} of ${testCaseCount}`, true);

    testCaseResponse = await TransformTestCaseResponse(testCaseResponse, false);
    traceLog(method, `Transformed test case ${testCaseIndex} of ${testCaseCount}`, true);
    traceLog(method, `Completed test case ${testCaseIndex} as ${testCaseCurrentPosition} of ${testCaseCount}`, true);
    if (TestCaseResponse.AddTestCase(testCaseResponse)) {
      await Excel.run(async (context) => {
        let resultWorksheet = await CreateOrReturnResultWorksheet(testCaseResponse.resultWorksheet, context);
        if (!resultWorksheet) throw new Error(`Could not create result worksheet ${testCaseResponse.resultWorksheet}`);
        //
        // Create a combined response array for all results + the header row
        //
        const combinedResponseArray = TestCaseResponse.GetCombinedResponseArray(testCaseResponse);
        if (testCaseResponse.testCaseConfig.debug) {
          combinedResponseArray.forEach((responseArray) => {
            traceLog(method, `LENGTH : ${responseArray.length} VALUES : ${responseArray.toString()}`);
          });
        }
        let fullTestCaseResponse = TestCaseResponse.CloneTestCaseResponse(testCaseResponse);
        fullTestCaseResponse.responseArray = combinedResponseArray;
        fullTestCaseResponse.rangeString = testCaseResponse.GetResultRange();
        if (fullTestCaseResponse.testCaseConfig.debug) {
          traceLog(method, fullTestCaseResponse.ToString());
        }
        traceLog(
          method,
          `Created full test case response with ${fullTestCaseResponse.responseArray.length} rows for range ${fullTestCaseResponse.rangeString}`
        );
        await WriteToResultWorksheet(fullTestCaseResponse, resultWorksheet);
        await UploadFilesFromIndexDb(fullTestCaseResponse);
        await CompletionActivities(fullTestCaseResponse, resultWorksheet);
        try {
          //
          // Check if any more batched Test cases need to be run
          //
          if (fullTestCaseResponse.HasFollowingTestCase()) {
            const nextTestCase = fullTestCaseResponse.GetFollowingTestCase();
            traceLog(method, `Found next test case : ${nextTestCase.ToString()}`);
            traceLog(method, `Running test case ${nextTestCase.index}`);
            await RunTest(nextTestCase, false);
            traceLog(method, `Completed test case ${nextTestCase.index}`);
          }
        } catch (error) {
          errorLog(error);
        }
      }).catch((error) => {
        errorLog(error);
        throw error;
      });
    }
  } catch (error) {
    errorLog(error);
  } finally {
    end(method);
  }
}

/**
 *
 * @param {*} testCaseResponse
 * @param {*} headerOnly
 * @returns testCaseResponse
 */
async function TransformTestCaseResponse(testCaseResponse, headerOnly = true) {
  const testCaseIndex = testCaseResponse.ordinalPosition;
  const method = `TransformTestCaseResponse[${testCaseIndex}]`;
  let result = null;
  const xml = TestCaseResponse.GetResponseXml(testCaseResponse);
  const expectedValues = testCaseResponse.expectedValues;
  const debug = testCaseResponse.testCaseConfig.debug;
  try {
    start(method);
    if (!testCaseResponse.HasExpectedValues()) {
      throw new Error(`Test case ${testCaseIndex} has no expected values`);
    }
    traceLog(method, `Generating test case response ...`);
    const domParser = new DOMParser();
    const xsltProcessor = new XSLTProcessor();

    let xsltStylesheet = await GetXslt(RESPONSE);
    xsltProcessor.importStylesheet(xsltStylesheet);

    xsltProcessor.setParameter(null, "include-header", headerOnly);
    xsltProcessor.setParameter(null, "testCase", testCaseIndex);
    // HRTT-109 optionally pass the debug flag
    if (debug) {
      traceLog(method, "Passing debug = true");
      xsltProcessor.setParameter(null, "debug", true);
    }
    const keysIterator = expectedValues.keys();
    const valuesIterator = expectedValues.values();
    for (let i = 0; i < expectedValues.size; i++) {
      const theKey = keysIterator.next().value;
      const theValue = valuesIterator.next().value;
      traceLog(method, `Set param ${theKey} = ${theValue}`);
      xsltProcessor.setParameter(null, theKey, theValue);
    }

    let xmlDoc = domParser.parseFromString(xml, "application/xml");
    result = xsltProcessor.transformToDocument(xmlDoc);
    if (result === null) {
      throw Error("Empty result for test case " + testCaseIndex);
    } else {
      result = result.firstElementChild.textContent.replace(/[\n\r]{1}/g, "");
      traceLog(method, `Generated test response ${result.substring(0, 20)}`);
      testCaseResponse.AddToResponseArray(result, headerOnly);
      return testCaseResponse;
    }
  } catch (err) {
    errorLog(err);
    throw err;
  } finally {
    end(method);
  }
} //TransformTestCaseResponse

/**
 *
 * @param {*} filenamePrefix
 * @param {*} testCaseRequest
 * @param {*} testCaseRange
 * @param {*} expectedResultRange
 * @param {*} isSource
 * @returns
 */
async function RangeToXml(filenamePrefix, testCaseRequest, testCaseRange, expectedResultRange, isSource = true) {
  var xml = "";
  const rowStartIndex = testCaseRequest.ordinalPosition;
  const rowEndIndex = testCaseRequest.ordinalPosition;
  const resultWorksheetName = testCaseRequest.resultWorksheet;
  const columnStartIndex = testCaseRequest.columnStartIndex;
  const functionName = `RangeToXml[${rowStartIndex}-${rowEndIndex}]`;
  const range = isSource ? testCaseRange : expectedResultRange;
  try {
    start(functionName);
    traceLog(
      functionName,
      `FILENAME PREFIX : "${filenamePrefix}" RANGE: \"${range.address}\" ROW START INDEX : ${rowStartIndex} ROW END INDEX : ${rowEndIndex} COLUMN START INDEX : ${columnStartIndex}`
    );

    xml = "<Worksheet>";
    const columnCount = range.columnCount;
    for (var r = rowStartIndex; r <= rowEndIndex; r++) {
      xml += "<Row>";
      for (var c = testCaseRequest.columnStartIndex; c < columnCount; c++) {
        const regexp = /[ <\(\)%#]{1,}/g;
        let fieldName = range.values[0][c].replace(regexp, "");
        let fieldValue = range.values[r][c];
        const regex2 = /[Dd]ate/g;
        const found = fieldName.match(regex2);
        fieldValue = new String(fieldValue).replaceAll("&", "&amp;");
        if (found !== null && found.length > 0) {
          try {
            if (!isNaN(fieldValue)) {
              var dateValue = ConvertDateExcel(fieldValue);
              fieldValue = dateValue;
              traceLog(functionName, `Converted ${fieldName} = ${fieldValue} to ${dateValue}`);
            }
          } catch (err) {
            errorLog(err);
          }
        }
        xml += "<" + fieldName + ">" + fieldValue + "</" + fieldName + ">";
      }
      xml += "</Row>";
    }
    xml += "</Worksheet>";

    //
    // Replace special characters
    //

    const replacementMap = new Map([
      ['"', "&quot;"],
      ["&", "&amp;"],
      ["'", "&apos;"],
      ["<", "&lt;"],
      [">", "&gt;"],
    ]);

    for (key in replacementMap.keys) {
      xml.replaceAll(key, replacementMap.get(key));
    }

    if ($("#debug").is(":checked")) {
      traceLog(functionName, `Source XML\n${xml}\n`);
    }
    //
    // Write intermediate to results
    //
    if (testCaseRequest.testCaseConfig.writeBaseRequest) {
      if (!resultWorksheetName) throw new Error(`Invalid result worksheet name`);
      const sharePointSite = await GetSharepointSite();
      const testCaseFolder = await GetTestCaseFolder(resultWorksheetName);
      const localAccessToken = testCaseRequest.accessToken;
      const payloadFilename = `${filenamePrefix}_${ColName(columnStartIndex)}${rowEndIndex}.xml`;
      const batchMode = testCaseRequest.IsBatch();
      const IsWritingFiles = TestCaseRequest.IsWritingFiles(testCaseRequest);

      traceLog(functionName, `Writing '${payloadFilename}' to '${testCaseFolder}'`);
      await WritePayloadFileAsync(
        sharePointSite,
        testCaseFolder,
        localAccessToken,
        payloadFilename,
        xml,
        batchMode && IsWritingFiles
      ).then(async () => {
        traceLog(functionName, `Wrote test cases to ${payloadFilename}`);
      });
    }
  } catch (err) {
    errorLog(err);
  } finally {
    end(functionName);
    return xml;
  }
} //RangeToXml

/**
 *
 * @param {*} testCaseResponse
 */
async function WriteResponsePayloadToSharePoint(testCaseResponse, file) {
  if (testCaseResponse?.testCaseConfig?.writeResponse) {
    const index = testCaseResponse.ordinalPosition;
    const method = `WriteResponsePayloadToSharePoint[${index}]`;
    const localAccessToken = testCaseResponse.accessToken;
    const sharePointSite = await GetSharepointSite();
    const testCaseFolder = await GetTestCaseFolder(testCaseResponse.testCaseStatistics.resultsWorksheet);
    const isBatch = TestCaseResponse.IsBatch(testCaseResponse);
    const isWritingFiles = TestCaseResponse.IsWritingFiles(testCaseResponse);

    await WritePayloadFileAsync(
      sharePointSite,
      testCaseFolder,
      localAccessToken,
      file,
      testCaseResponse.responseXml,
      isBatch && isWritingFiles
    )
      .then(async () => {
        const message = isBatch
          ? `Cached response payload using index "${sharePointSite}${testCaseFolder}" for batch ZIP upload`
          : `Wrote test case name "${file}" to "${testCaseFolder}"`;
        traceLog(method, message);
      })
      .catch(async (error) => {
        errorLog(error);
        throw error;
      })
      .catch(async (error) => {
        errorLog(error);
        throw error;
      });
  }
}

/**
 * Create a new worksheet, if required for the test results.
 * @param {*} resultWorksheetName
 * @param {*} context
 * @returns
 */
async function CreateOrReturnResultWorksheet(resultWorksheetName, context) {
  start(`CreateResultWorksheet[${resultWorksheetName}]`);
  try {
    let resultWorksheet = context.workbook.worksheets.getItemOrNullObject(resultWorksheetName);
    await context.sync();
    if (resultWorksheet.isNullObject) {
      resultWorksheet = context.workbook.worksheets.add(resultWorksheetName);
      resultWorksheet.load("name");
      await context.sync();
    }
    return resultWorksheet;
  } catch (error) {
    throw error;
  } finally {
    end(`CreateResultWorksheet[${resultWorksheetName}]`);
  }
} //CreateResultWorksheet

/**
 *
 * @param {*} testStatistics
 * @param {*} context
 * @param {*} testHistoryWorksheet
 * @param {*} historyTable
 */
async function AddHistoryRowToTable(testCaseResponse, context, testHistoryWorksheet, historyTable) {
  start(`AddHistoryRowToTable`);
  const name = GetAccount().name;
  const email = GetAccount().username;
  const testStatistics = testCaseResponse.testCaseStatistics;
  const testCaseConfig = testCaseResponse.testCaseConfig;
  const runDate = GetRunDateFormat(testStatistics);
  const startTime = GetStartTimeFormat(testStatistics);
  let newRow = [
    [
      testStatistics.resultsWorksheet, //Results Worksheet (A)
      runDate, //Run Date (B)
      startTime, //Start Datetime (C)
      testStatistics.endTime, //End Datetime (D)
      testStatistics.elapsed, //Elapsed (E)
      name, // Name (F)
      email, // Email (G)
      testStatistics.tr4eSubscription?.name, // TR4E Subscription (if enabled) (H)
      _testEnvironmentMap.get(testStatistics.environment).name, //Environment Name (I)
      testStatistics.environment, //Environment URL (J)
      testStatistics.firstTestCase, //First Test Case (K)
      testStatistics.lastTestCase, // Last Test Case (L)
      testStatistics.batchSize, // Batch Size (M)
      testStatistics.staggerDelay, // Stagger Delay used for batch run (N)
      testCaseConfig.simulation, //Simulation (O)
      testCaseConfig.parallelExecution, // Parallel Execution (P)
      testCaseConfig.debug, // Debug (Q)
      testCaseConfig.resultsInSeparateTable, // Results in Separate Table (R)
      testCaseConfig.writeBaseRequest, // Write Base Request (S)
      testCaseConfig.writeRequest, // Write Request (T)
      testCaseConfig.writeResponse, // Write Response (U)
    ],
  ];
  historyTable.rows.add(
    null, // index, Adds rows to the end of the table.
    newRow,
    true // alwaysInsert, Specifies that the new rows be inserted into the table.
  );
  testHistoryWorksheet.getUsedRange().format.autofitColumns();
  testHistoryWorksheet.getUsedRange().format.autofitRows();

  await context.sync();
  end(`AddHistoryRowToTable`);
}

/**
 * Creates a test history entry in the "Test History" worksheet. If one doesn't exist then it creates a new worksheet
 * @param {*} context
 * @param {*} worksbook
 */
async function AddTestHistoryEntry(testCaseResponse) {
  await Excel.run(async (context) => {
    let historyTableName = TEST_HISTORY.replaceAll(" ", "_");
    let testHistoryWorksheet = context.workbook.worksheets.getItemOrNullObject(TEST_HISTORY);
    testHistoryWorksheet.load("name");
    testHistoryWorksheet.load("position");
    await context.sync();

    if (testHistoryWorksheet.isNullObject) {
      try {
        let sheets = context.workbook.worksheets;
        let newSheet = sheets.add(TEST_HISTORY);
        newSheet.position = 0;
        newSheet.load("name");
        newSheet.load("position");
        await context.sync();
        traceLog(`AddTestHistoryEntry`, `Added worksheet named "${newSheet.name}" in position ${newSheet.position}`);

        let existingSheet = context.workbook.worksheets.getItem(TEST_HISTORY);
        const rangeString = `A1:${ColName(TEST_HISTORY_HEADER.length - 1)}1`;
        let newTable = existingSheet.tables.add(rangeString, true /* has headers */);
        newTable.name = historyTableName;
        newTable.getHeaderRowRange().values = [TEST_HISTORY_HEADER];
        await AddHistoryRowToTable(testCaseResponse, context, newSheet, newTable);
      } catch (error) {
        errorLog(error);
      }
    } else {
      traceLog(`AddTestHistoryEntry`, `Found "${testHistoryWorksheet.name}" at ${testHistoryWorksheet.position}`);
      let historyTable = testHistoryWorksheet.tables.getItem(historyTableName);
      await AddHistoryRowToTable(testCaseResponse, context, testHistoryWorksheet, historyTable);
    }
  });
}

/**
 *
 * @param {*} testCaseResponse
 * @param {*} resultWorksheet
 */
async function ConvertResultRangeToTable(testCaseResponse, resultWorksheet) {
  const method = `ConvertResultRangeToTable[${testCaseResponse.ordinalPosition}]`;
  const resultRange = testCaseResponse.GetResultRange();
  start(method);
  try {
    //
    // Do not convert if there are already separate tables created in WriteToResultWroksheet
    //
    if (testCaseResponse.testCaseConfig.resultsInSeparateTable) return;
    const testEnvironment = testCaseResponse.testEnvironmentMap.get(testCaseResponse.testEnvironmentURL);
    let table = resultWorksheet.tables.add(resultRange, true);
    table.sort.apply(
      [
        {
          key: 0,
          ascending: true,
          sortOn: "Value",
        },
      ],
      true
    );
    //table.name = testCaseResponse.resultWorksheet;
    const tableStyle = testEnvironment.tableStyle;
    table.style = tableStyle;
    table.load("tableStyle");
    await resultWorksheet.context.sync();
    traceLog(method, `Created table for range ${resultRange} with style "${table.style}"`);
  } catch (err) {
    errorLog(err);
  } finally {
    end(method);
  }
}

/**
 * Write the test case output to the test run result worksheet. Each test case will be created as a separate table
 * @param {*} testCaseResponse
 * @param {*} resultWorksheet
 */
async function WriteToResultWorksheet(testCaseResponse, resultWorksheet) {
  const method = `WriteToResultWorksheet[${testCaseResponse.ordinalPosition}]`;
  start(method);
  try {
    const testEnvironment = testCaseResponse.testEnvironmentMap.get(testCaseResponse.testEnvironmentURL);
    const rangeString = testCaseResponse.rangeString;
    const footerRangeString = testCaseResponse.footerRangeString;
    const responseArray = testCaseResponse.responseArray;
    traceLog(
      method,
      `Writing ${responseArray.length} rows ${testCaseResponse.GetResponseArrayLength()} cols for range ${rangeString}`
    );

    let resultRange = resultWorksheet.getRange(rangeString);
    resultRange.values = responseArray;

    resultWorksheet.getUsedRange().format.autofitColumns();
    resultWorksheet.getUsedRange().format.autofitRows();
    resultWorksheet.activate();

    //
    // If creating separate tables per test case
    //
    if (testCaseResponse.testCaseConfig.resultsInSeparateTable) {
      const resultRange = testCaseResponse.resultRange;
      let table = resultWorksheet.tables.add(resultRange, true);

      const tableStyle = testEnvironment.tableStyle;
      table.style = tableStyle;
      table.load("tableStyle");
      await resultWorksheet.context.sync();
      traceLog(method, `Created table for range ${resultRange} with style "${table.style}"`);
    }

    //
    // Only write separator where creating separate tables
    //
    if (footerRangeString !== null) {
      let footerRange = resultWorksheet.getRange(footerRangeString);
      footerRange.insert(Excel.InsertShiftDirection.down);
    }

    const worksheetTabColor = testEnvironment.worksheetTabColor;
    resultWorksheet.tabColor = worksheetTabColor;
    await resultWorksheet.context.sync();
  } catch (err) {
    errorLog(err);
    //throw(err);
  } finally {
    end(method);
  }
}

/**
 * Enables spinner to show test is running
 * @param {*} elementId
 * @param {*} enable
 */
function ToggleSpinner(elementId, enable = true) {
  let spinner = $(elementId);
  enable ? spinner.removeClass("d-none") : spinner.addClass("d-none");
}

/**
 *
 * @param {*} testCase
 * @param {*} useSeparateTable
 */
async function ProcessTestCaseBatch(testCase) {
  const method = `ProcessTestCaseBatch[${testCase.ordinalPosition}]`;
  start(method);
  try {
    const serialized = TestCaseRequest.Serialize(testCase);
    if (testCase.testCaseConfig.debug) {
      traceLog(method, testCase.ToString());
      traceLog(method, `TEST CASE : ${serialized.testCase}`);
      traceLog(method, `EXPECTED VALUES : ${serialized.expectedValues}`);
      traceLog(method, `ENVIRONMENT MAP : ${serialized.testEnvironmentMap}`);
      traceLog(method, `SIMULATION MAP : ${serialized.simulationMap}`);
    }
    _testCaseWorker.postMessage(serialized);
  } catch (error) {
    errorLog(error);
    throw error;
  } finally {
    end(method);
  }
}
/**
 * Sequential Test Case Response Handler
 * @param {*} testCaseResponse
 */
const TestCaseResponseHandler = async (testCaseResponse) => {
  const testCaseIndex = testCaseResponse.testCase.ordinalPosition;
  const methodName = `TestCaseResponseHandler[${testCaseIndex}]`;
  if (testCaseResponse.IncludeHeader()) {
    //
    // This is the first test case, hence need to write the header row as well as the first row of data:
    // 1. Transform the header row to CSV
    // 2. Transform the first data row to CSV
    // 3. Write the header and first data row to the result worksheet
    // 4. If there are no more test cases then call CompletionActivities
    //
    const testCaseResponseWithHeader = await TransformTestCaseResponse(testCaseResponse);
    const testCaseResponseWithHeaderAndData = await TransformTestCaseResponse(testCaseResponseWithHeader, false);
    await Excel.run(async (context) => {
      const resultWorksheet = await CreateOrReturnResultWorksheet(testCaseResponse.resultWorksheet, context);
      await WriteToResultWorksheet(testCaseResponseWithHeaderAndData, resultWorksheet);
      traceLog(
        methodName,
        `Completed test case ${
          testCaseResponseWithHeaderAndData.ordinalPosition
        } as ${TestCaseResponse.GetProcessedResponseCount()} of ${testCaseResponse.testCaseCount}`,
        true
      );
      if (TestCaseResponse.AddTestCase(testCaseResponseWithHeaderAndData)) {
        await CompletionActivities(testCaseResponseWithHeaderAndData, resultWorksheet);
      }
    });
  } else {
    //
    // This is *not* the first test case, hence need to write second or subsequent row of data:
    // 1. Transform the subsequent data row to CSV
    // 2. Write the subsequent data row to the result worksheet
    // 3. If there are no more test cases then call CompletionActivities
    //
    const testCaseResponseWithDataOnly = await TransformTestCaseResponse(testCaseResponse, false);
    await Excel.run(async (context) => {
      const resultWorksheet = await CreateOrReturnResultWorksheet(testCaseResponse.resultWorksheet, context);
      await WriteToResultWorksheet(testCaseResponseWithDataOnly, resultWorksheet);
      traceLog(
        methodName,
        `Completed test case ${
          testCaseResponseWithDataOnly.ordinalPosition
        } as ${TestCaseResponse.GetProcessedResponseCount()} of ${testCaseResponse.testCaseCount}`,
        true
      );
      if (TestCaseResponse.AddTestCase(testCaseResponseWithDataOnly)) {
        await CompletionActivities(testCaseResponseWithDataOnly, resultWorksheet);
      }
    });
  }
}; //TestCaseResponseHandler

/**
 *
 * @param {*} error
 */
const TestCaseErrorHandler = async (error) => {
  traceLog("TestCaseErrorHandler", `${error}`);
  throw error;
}; //TestCaseErrorHandler

/**
 *
 * @param {*} testCase
 * @param {*} useSeparateTable
 */
async function ProcessTestCaseSequentially(testCase) {
  const testCaseIndex = testCase.ordinalPosition;
  const methodName = `ProcessTestCaseSequentially[${testCaseIndex}]`;
  start(methodName);
  try {
    //
    // Serialize / Deserialize map to ensure string keys
    //
    testCase.simulationMap = DeserializeMap(SerializeMap(testCase.simulationMap));
    await ExecuteTestCase(testCase, TestCaseResponseHandler, TestCaseErrorHandler);
  } catch (error) {
    errorLog(error);
    throw error;
  } finally {
    end(methodName);
  }
} // ProcessTestCaseSequentially

/**
 *
 * @param {*} testCase
 */
async function ProcessTestCase(testCase) {
  const resultWorksheet = testCase.resultWorksheet;
  const isFirstRow = testCase.IsFirstRow();
  const testCaseIndex = testCase.ordinalPosition;
  const responseWorksheetRow = testCase.GetWorksheetRow();
  const expectedValues = testCase.expectedValues;
  const methodName = `ProcessTestCase[${testCaseIndex}]`;
  start(methodName);
  try {
    traceLog(
      methodName,
      `WORKSHEET NAME : "${resultWorksheet}" TEST CASE LENGTH : ${testCase.length} IS FIRST ROW ${isFirstRow} TEST CASE INDEX : ${testCaseIndex} RESPONSE WORKSHEET ROW : ${responseWorksheetRow} EXPECTED VALUES SIZE : ${expectedValues.size}`
    );
    //
    // Important that the Service API Access Token (s) is created and cached before the test case is processed.
    //
    const useTr4e = TestCaseConfig.UseTr4eSubscription(testCase.testCaseConfig);
    if (useTr4e) {
      await TestCaseRequest.GetServiceApiAccessToken(testCase);
    }
    await TestCaseRequest.GetServiceApiAccessToken(testCase, false);
    if (testCase.testCaseConfig.parallelExecution) {
      await ProcessTestCaseSequentially(testCase);
    } else {
      await ProcessTestCaseBatch(testCase);
    }
  } catch (err) {
    errorLog(err);
    throw err;
  } finally {
    end(methodName);
  }
}

/**
 * testCase.ordinalPosition, expectedXml, testCase.expectedResultRange.columnCount
 * @param {*} testCase
 * @returns
 */
function GenerateExpectedValues(expectedXml, columnCount) {
  start(`GenerateExpectedValues`);
  try {
    const domParser = new DOMParser();
    const document = domParser.parseFromString(expectedXml, "application/xml");
    const retVal = new Map();
    for (let i = 0; i < columnCount; i++) {
      const valueXpath = `/Worksheet/Row/*[position()=${i + 1}]`;
      const nameXpath = `name(${valueXpath})`;
      const expectedName = document.evaluate(nameXpath, document, null, XPathResult.STRING_TYPE, null).stringValue;
      const expectedValue = document.evaluate(valueXpath, document, null, XPathResult.STRING_TYPE, null).stringValue;
      traceLog(`GenerateExpectedValues`, `Adding ${expectedName} = ${expectedValue}`);
      retVal.set(expectedName, expectedValue);
    }
    traceLog(`GenerateExpectedValues`, `Created map size ${retVal.size}`);
    return retVal;
  } catch (error) {
    errorLog(error);
    throw error;
  } finally {
    end(`GenerateExpectedValues`);
  }
}

/**
 *
 * @param {*} testCaseRequest
 * @returns
 */
async function TestCasePreparation(testCaseRequest, testCaseRange, expectedResultRange) {
  const methodName = `TestCasePreparation[${testCaseRequest.ordinalPosition}]`;
  start(methodName);
  if (!testCaseRequest) throw new Error(`Empty test case`);
  try {
    //
    // Test case must be processed from #1 on
    //
    traceLog(
      methodName,
      `WORKSHEET NAME : "${testCaseRequest.resultWorksheet}" TEST CASE RANGE : \"${testCaseRange.address}\" EXPECTED RESULT RANGE: \"${expectedResultRange.address}\" COLUMN START INDEX : ${testCaseRequest.columnStartIndex} ORDINAL POSITION : ${testCaseRequest.ordinalPosition}`
    );
    testCaseRequest.sourceXml = await RangeToXml(`rawRequest`, testCaseRequest, testCaseRange, expectedResultRange);
    testCaseRequest.expectedXml = await RangeToXml(
      `rawExpected`,
      testCaseRequest,
      testCaseRange,
      expectedResultRange,
      false
    );
    testCaseRequest.expectedValues = GenerateExpectedValues(
      testCaseRequest.expectedXml,
      expectedResultRange.columnCount
    );
    if (!testCaseRequest.HasExpectedValues()) throw new Error(`Test case ${testCaseRequest.ordinalPosition}`);
    traceLog(
      methodName,
      `SOURCE XML SIZE : ${testCaseRequest.sourceXml.length} EXPECTED XML SIZE : ${testCaseRequest.expectedXml.length} EXPECTED VALUES : ${testCaseRequest.expectedValues.size}`
    );
    return testCaseRequest;
  } finally {
    end(methodName);
  }
}

const MAXIMUM_BATCH_SIZE_TR4E_OFF = 100;

/**
 * If "serial execution" then set "batch size" = "test case count"
 * else if "batch size dropdown" is not a valid number then set "batch size" = "test case count"
 * else set "batch size" = "batch size dropdown"
 *
 * @param {*} startPosition = GetStart() - 1;
 * @param {*} firstTestCaseIndex = GetStart();
 * @param {*} endPosition = GetEnd();
 * @param {*} lastTestCaseIndex = GetEnd();
 * @param {*} testCaseCount = GetEnd() - GetStart() + 1;
 * @param {*} serialExecution
 */
async function CreateTestCases(
  startPosition,
  firstTestCaseIndex,
  endPosition,
  lastTestCaseIndex,
  testCaseCount,
  serialExecution
) {
  start("CreateTestCases");

  /**
   *
   * @param {*} serialExecution
   * @param {*} testCaseCount
   * @returns
   */
  let safeBatchSize = (serialExecution, testCaseCount) => {
    const baseValue = $("#batchSelector").find(":selected").val();
    const regexp = /[\d]{1,}\.?[\d]{0,}/;
    let retVal = testCaseCount;
    if (serialExecution) {
      //
      // Serial execution - do all test cases one after another
      //
      retVal = testCaseCount;
    } else if (!IsTr4eSubscriptionEnabled()) {
      //
      // If TR4E subscription is disabled then the batch size cannot exceed 100
      //
      const convertedNumber = Number.parseInt(baseValue);
      if (convertedNumber <= MAXIMUM_BATCH_SIZE_TR4E_OFF) {
        retVal = convertedNumber;
      } else {
        retVal = MAXIMUM_BATCH_SIZE_TR4E_OFF;
      }
    } else if (Number.isInteger(baseValue)) {
      retVal = baseValue;
    } else if (baseValue.match(regexp)) {
      const convertedNumber = Number.parseInt(baseValue);
      if (Number.isInteger(convertedNumber)) {
        retVal = convertedNumber;
      }
    }
    console.log(
      "safeBatchSize",
      `SERIAL EXECUTION = ${serialExecution} TR4E ENABLED = ${IsTr4eSubscriptionEnabled()} TEST CASE COUNT = ${testCaseCount} BATCH SIZE = ${retVal}`
    );
    return retVal;
  };

  const batchSize = safeBatchSize(serialExecution, testCaseCount);
  const batchCount = serialExecution ? 1 : Math.ceil(testCaseCount / batchSize);
  try {
    let previousTestCase = null;
    let firstTestCase = null;
    for (i = 0; i < batchCount; i++) {
      const batchNumber = i + 1;
      const batchStart = i * batchSize + Number(startPosition);
      const batchFirstTestCaseIndex = i * batchSize + Number(firstTestCaseIndex);
      const batchEnd = batchStart + batchSize > endPosition ? endPosition : batchStart + batchSize;
      const isLast = i == batchCount - 1;
      const batchTestCaseCount = batchEnd - batchStart;
      const testCase = new TestCase(batchNumber, batchFirstTestCaseIndex, batchEnd, batchTestCaseCount, isLast);
      if (firstTestCase == null) {
        firstTestCase = testCase;
      }
      if (previousTestCase != null) {
        previousTestCase.SetNextTestCase(testCase);
      }
      traceLog(`CreateTestCases`, `Created test case ${testCase.ToString()}`);
      previousTestCase = testCase;
    }
    traceLog(`CreateTestCases`, `Running test case ${firstTestCase.index}`);
    await RunTest(firstTestCase);
    traceLog(`CreateTestCases`, `Completed test case ${firstTestCase.index}`);
  } catch (error) {
    errorLog(error);
    throw error;
  } finally {
    end("CreateTestCases");
  }
} //CreateBatches

/**
 *
 * @param {TestCase} testCase
 * @param {boolean} isFirst
 * @returns
 */
async function RunTest(testCase, isFirst = true) {
  start("RunTest");
  ToggleSpinner("#run-spinner");
  const startPosition = testCase.GetZeroOffsetStart();
  const firstTestCaseIndex = testCase.start;
  const endPosition = testCase.end;
  const lastTestCaseIndex = testCase.end;
  const testCaseCount = testCase.size;
  const url = $("input[type='radio'][name='environmentRadio'].form-check-input:checked").val();
  const userPrincipal = $("#userPrincipal").val();
  const userSecret = $("#userSecret").val();
  const authType = $('input[name="securityRadio"]:checked').val();
  //
  // If the subscription is enabled then pass the key
  //
  const tr4eSubscriptionKey = IsTr4eSubscriptionKeyValid() ? $("#subscriptionKey").val() : "";
  const tr4eSubscriptionName = IsTr4eSubscriptionNameValid() ? $("#subscriptionName").val() : "";
  const tr4eSubscriptionEnabled = $("#subscriptionRadioBasic").is(":checked");
  let tr4eSubscriptionValidated = tr4eSubscriptionEnabled;

  //
  // If this is the first batch then validate the TR4E subscription. Otherwise assume that it is ok.
  //
  let tr4eSubscription = null;
  if (isFirst && tr4eSubscriptionEnabled) {
    tr4eSubscriptionValidated = await PingTr4e();

    if (!tr4eSubscriptionValidated) {
      ToggleSpinner("#run-spinner", false);
      return;
    }
    tr4eSubscription = new Tr4eSubscription(
      tr4eSubscriptionName,
      tr4eSubscriptionKey,
      tr4eSubscriptionEnabled,
      tr4eSubscriptionValidated
    );
  } else if (tr4eSubscriptionEnabled) {
    tr4eSubscription = new Tr4eSubscription(tr4eSubscriptionName, tr4eSubscriptionKey, tr4eSubscriptionEnabled, true);
  }

  traceLog(
    `RunTest`,
    `START POSITION : ${startPosition} FIRST TEST CSE INDEX : ${firstTestCaseIndex} END POSITION : ${endPosition} LAST TEST CASE INDEX : ${lastTestCaseIndex} TEST CASE COUNT : ${testCaseCount}`
  );

  const testCaseConfig = new TestCaseConfig(
    tr4eSubscription,
    authType,
    _clientId,
    _redirectUri,
    _scope,
    url,
    userPrincipal,
    userSecret,
    $("#simulation").is(":checked"),
    $("#parallel").is(":checked"),
    $("#delaySelector").find(":selected").val(),
    $("#debug").is(":checked"),
    $("#resultsSeparateTable").is(":checked"),
    $("#writeBaseRequest").is(":checked"),
    $("#writeRequest").is(":checked"),
    $("#writeResponse").is(":checked")
  );

  //
  // If running simulations and the map is empty then reload
  //
  if ($("#simulation").is(":checked") && _simulationMap.size == 0) {
    await LoadSharePointSimulationFolder();
  }

  //
  // For TR4E ensure that we reset everything before the next run
  //
  if (TestCaseConfig.UseTr4eSubscription(testCaseConfig)) {
    TestCaseResponse.Reset();
  }

  await Excel.run(async (context) => {
    let configSheet = context.workbook.worksheets.getItem(TR4E_CONFIG_WORKSHEET);
    let range = configSheet.getRange(TR4E_TEST_CONFIG);
    range.load("values");
    await context.sync();

    let sourceWorksheetName = range.values[0][0];
    let sourceCells = range.values[1][0];
    let expectedResultCells = range.values[2][0];
    let sourceWorksheet = context.workbook.worksheets.getItem(sourceWorksheetName);
    let testCaseRange = sourceWorksheet.getRange(sourceCells);
    let expectedResultRange = sourceWorksheet.getRange(expectedResultCells);
    let ranges = new Array();
    ranges.push(testCaseRange, expectedResultRange);

    for (let i = 0; i < ranges.length; i++) {
      ranges[i].load("values");
      ranges[i].load("rowCount");
      ranges[i].load("columnCount");
      ranges[i].load("address");
    }
    await context.sync();

    traceLog(
      `RunTest`,
      `worksheet NAME : ${sourceWorksheetName}\n\tTEST RANGE : ${sourceCells}\n\tTEST ROWS : ${testCaseRange.rowCount}\n\tTEST COLUMNS : ${testCaseRange.columnCount}` +
        `\n\tEXPECTED RANGE : ${expectedResultCells}\n\tEXPECTED ROWS : ${expectedResultRange.rowCount}\n\tEXPECTED COLUMNS : ${expectedResultRange.columnCount}`
    );

    const columnStartIndex = 0;
    const startTime = new Date();
    const runDateFormat = startTime.format("d mmm yyyy");
    const startTimeFormat = startTime.format("longTime");
    const resultWorksheetName = `${runDateFormat} ${startTimeFormat.replaceAll(":", ".")}`;
    /**
     *
     * @param {*} resultsWorksheet
     * @param {*} runDateFormat
     * @param {*} startTimeFormat
     * @param {*} startTime
     * @param {*} environment
     * @param {*} firstTestCase
     * @param {*} lastTestCase
     * @param {*} testCaseCount
     */
    const testCaseStatistics = new TestCaseStatistics(
      resultWorksheetName,
      runDateFormat,
      startTimeFormat,
      startTime,
      _testEnvironmentURL,
      firstTestCaseIndex,
      lastTestCaseIndex,
      testCaseCount,
      tr4eSubscription,
      testCase.size,
      testCaseConfig.staggerDelay
    );
    if (testCaseConfig.debug) {
      traceLog(`RunTest`, `CONFIG : ${testCaseConfig.ToString()}`);
      traceLog(`RunTest`, `STATS : ${testCaseStatistics.ToString()}`);
    }

    traceLog(`RunTest`, `Creating ${testCaseCount} test cases for ${resultWorksheetName}`, true);

    try {
      for (var i = startPosition; i < endPosition; i++) {
        const ordinalPosition = i + 1;
        const accessToken = await GetAccessToken();
        const targetPayloadFormat = $("input[type=radio][name=formatRadio]:checked").val();

        /**
         *
         * @param {*} testCaseConfig
         * @param {*} testCaseStatistics
         * @param {*} resultWorksheet
         * @param {*} columnStartIndex
         * @param {*} ordinalPosition
         * @param {*} startPosition
         * @param {*} accessToken
         * @param {*} sourceXml
         * @param {*} expectedXml
         * @param {*} expectedValues
         * @param {*} testCaseCount
         * @param {*} testEnvironmentURL
         * @param {*} simulationMap
         * @param {*} siteRelativeUrl
         * @param {*} serverUrl
         */
        let testCaseRequest = new TestCaseRequest(
          testCaseConfig,
          testCaseStatistics,
          resultWorksheetName,
          columnStartIndex,
          ordinalPosition,
          startPosition,
          accessToken,
          targetPayloadFormat,
          null,
          null,
          null,
          testCaseCount,
          _testEnvironmentURL,
          _testEnvironmentMap,
          _simulationMap,
          _siteRelativeUrl,
          _serverUrl
        );
        testCaseRequest.SetTestCase(testCase);
        testCaseRequest = await TestCasePreparation(testCaseRequest, testCaseRange, expectedResultRange);
        testCaseRequest = await TransformTestCaseRequest(testCaseRequest);
        await ProcessTestCase(testCaseRequest);
        const displayMessage = testCaseRequest.IsBatch();
        const completionMessage = `Submitted test case ${i - startPosition + 1} of ${testCaseCount}`;
        traceLog(`RunTest`, completionMessage, displayMessage);
      } // for
    } catch (error) {
      errorLog(`Stopping run due to ${error.message}`);
      ToggleSpinner("#run-spinner", false);
    } finally {
      end("RunTest");
    }
  }).then(async () => {});
}

/**
 *
 */
async function UploadFilesFromIndexDb(testCaseResponse) {
  try {
    start(`UploadFilesFromIndexDb`);
    const sharePointSite = await GetSharepointSite();
    const testCaseFolder = await GetTestCaseFolder(testCaseResponse.testCaseStatistics.resultsWorksheet);
    const queryParams = [`${sharePointSite}`, `${testCaseFolder}`];

    db.transaction("rw", db.tr4eFileStore, () => {
      //
      // Query DB for files associated with this run
      //
      return db.tr4eFileStore
        .where("[server+folder]")
        .equals(queryParams)
        .toArray()
        .then((files) => {
          var zip = new JSZip();

          //
          // Iterate over returned files, add to ZIP and remove from IndexDB
          //
          files.forEach(function (savedFile) {
            const date = savedFile.date;
            const file = savedFile.file;
            const arrayBuffer = savedFile.arrayBuffer;
            const message = `DATE : ${savedFile.date.toISOString()} SERVER : ${savedFile.server} FOLDER : ${
              savedFile.folder
            } FILE : ${file} ARRAY BUFFER SIZE : ${savedFile.arrayBuffer.length}`;
            zip.file(file, arrayBuffer);
            traceLog(`UploadFilesFromIndexDb`, `ZIP added ${message}`);
          }); // files.forEach

          //
          // Remove the cached files from indexdb
          //
          db.tr4eFileStore
            .where("[server+folder]")
            .equals(queryParams)
            .delete()
            .then(function (deleteCount) {
              traceLog(
                `UploadFilesFromIndexDb`,
                `IndexDB site ${queryParams[0]} folder ${queryParams[1]} deleted ${deleteCount}`
              );
            });

          //
          // Only write the ZIP file where there were files
          //
          if (files?.length > 0) {
            zip
              .generateAsync({
                type: "uint8array",
                compression: "DEFLATE",
                compressionOptions: {
                  level: 5,
                },
              })
              .then((blob) => {
                //
                // Create an event for the ZIP file
                //
                const event = {
                  serverUrl: sharePointSite,
                  serverRelativeUrlToFolder: testCaseFolder,
                  AccessToken: testCaseResponse.accessToken,
                  fileInput: `${testCaseResponse.resultWorksheet}.zip`,
                  arrayBuffer: blob,
                };
                _writePayloadWorker.postMessage(event);
              }); // zip.generateAsync
          } // fi
        }); // toArray()
    }).catch((err) => {
      errorLog(err);
    }); //db.transaction
  } catch (error) {
    errorLog(error);
    throw error;
  } finally {
    end(`UploadFilesFromIndexDb`);
  }
}

/**
 * Completion activities performed when the last test case has been processed
 * @param {*} testStatistics
 */
async function CompletionActivities(testCaseResponse, resultWorksheet) {
  if (TestCaseResponse.LastTestCaseProcessed(testCaseResponse)) {
    try {
      start(`CompletionActivities`);
      const endTime = new Date();
      const startTime = testCaseResponse.testCaseStatistics.startTime;
      testCaseResponse.testCaseStatistics.endTime = endTime.format("longTime");
      testCaseResponse.testCaseStatistics.elapsed = DateDiffInDays(endTime, startTime);
      testCaseResponse.testCaseStatistics.testCaseCount = testCaseResponse.testCaseCount;
      await ConvertResultRangeToTable(testCaseResponse, resultWorksheet);
      await AddTestHistoryEntry(testCaseResponse);
      TestCaseResponse.Reset();
      //
      // Disable spinner
      //
      const testCaseCount = testCaseResponse.testCaseStatistics.testCaseCount;
      const elapsed = testCaseResponse.testCaseStatistics.elapsed.toLocaleString(`en-AU`);
      traceLog(`CompletionActivities`, `Completed ${testCaseCount} tests in ${elapsed}ms`, true);
    } catch (error) {
      errorLog(error);
      throw error;
    } finally {
      ToggleSpinner("#run-spinner", false);
      end(`CompletionActivities`);
    }
  }
}

/**
 *
 * @param {*} a
 * @param {*} b
 * @returns
 */
function DateDiffInDays(a, b) {
  //const _MS_PER_DAY = 1000 * 60 * 60 * 24;
  // Discard the time and time-zone information.
  //const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  //const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

  return Math.abs(a.getTime() - b.getTime());
}

/**
 * Rate Test Tool
 */

/** Default helper for invoking an action and handling errors. */
async function tryCatch(callback) {
  try {
    await callback();
  } catch (error) {
    // Note: In a production add-in, you'd want to notify the user through your add-in's UI.
    console.error(error);
  }
}

/*
 * Date Format 1.2.3
 * (c) 2007-2009 Steven Levithan <stevenlevithan.com>
 * MIT license
 *
 * Includes enhancements by Scott Trenda <scott.trenda.net>
 * and Kris Kowal <cixar.com/~kris.kowal/>
 *
 * Accepts a date, a mask, or a date and a mask.
 * Returns a formatted version of the given date.
 * The date defaults to the current date/time.
 * The mask defaults to dateFormat.masks.default.
 */

var dateFormat = (function () {
  var token = /d{1,4}|m{1,4}|yy(?:yy)?|([HhMsTt])\1?|[LloSZ]|"[^"]*"|'[^']*'/g,
    timezone =
      /\b(?:[PMCEA][SDP]T|(?:Pacific|Mountain|Central|Eastern|Atlantic) (?:Standard|Daylight|Prevailing) Time|(?:GMT|UTC)(?:[-+]\d{4})?)\b/g,
    timezoneClip = /[^-+\dA-Z]/g,
    pad = function (val, len) {
      val = String(val);
      len = len || 2;
      while (val.length < len) val = "0" + val;
      return val;
    };

  // Regexes and supporting functions are cached through closure
  return function (date, mask, utc) {
    var dF = dateFormat;

    // You can't provide utc if you skip other args (use the "UTC:" mask prefix)
    if (arguments.length == 1 && Object.prototype.toString.call(date) == "[object String]" && !/\d/.test(date)) {
      mask = date;
      date = undefined;
    }

    // Passing date through Date applies Date.parse, if necessary
    date = date ? new Date(date) : new Date();
    if (isNaN(date)) throw SyntaxError("invalid date");

    mask = String(dF.masks[mask] || mask || dF.masks["default"]);

    // Allow setting the utc argument via the mask
    if (mask.slice(0, 4) == "UTC:") {
      mask = mask.slice(4);
      utc = true;
    }

    var _ = utc ? "getUTC" : "get",
      d = date[_ + "Date"](),
      D = date[_ + "Day"](),
      m = date[_ + "Month"](),
      y = date[_ + "FullYear"](),
      H = date[_ + "Hours"](),
      M = date[_ + "Minutes"](),
      s = date[_ + "Seconds"](),
      L = date[_ + "Milliseconds"](),
      o = utc ? 0 : date.getTimezoneOffset(),
      flags = {
        d: d,
        dd: pad(d),
        ddd: dF.i18n.dayNames[D],
        dddd: dF.i18n.dayNames[D + 7],
        m: m + 1,
        mm: pad(m + 1),
        mmm: dF.i18n.monthNames[m],
        mmmm: dF.i18n.monthNames[m + 12],
        yy: String(y).slice(2),
        yyyy: y,
        h: H % 12 || 12,
        hh: pad(H % 12 || 12),
        H: H,
        HH: pad(H),
        M: M,
        MM: pad(M),
        s: s,
        ss: pad(s),
        l: pad(L, 3),
        L: pad(L > 99 ? Math.round(L / 10) : L),
        t: H < 12 ? "a" : "p",
        tt: H < 12 ? "am" : "pm",
        T: H < 12 ? "A" : "P",
        TT: H < 12 ? "AM" : "PM",
        Z: utc ? "UTC" : (String(date).match(timezone) || [""]).pop().replace(timezoneClip, ""),
        o: (o > 0 ? "-" : "+") + pad(Math.floor(Math.abs(o) / 60) * 100 + (Math.abs(o) % 60), 4),
        S: ["th", "st", "nd", "rd"][d % 10 > 3 ? 0 : (((d % 100) - (d % 10) != 10) * d) % 10],
      };

    return mask.replace(token, function ($0) {
      return $0 in flags ? flags[$0] : $0.slice(1, $0.length - 1);
    });
  };
})();

// Some common format strings
dateFormat.masks = {
  default: "ddd mmm dd yyyy HH:MM:ss",
  shortDate: "m/d/yy",
  mediumDate: "mmm d, yyyy",
  longDate: "mmmm d, yyyy",
  fullDate: "dddd, mmmm d, yyyy",
  shortTime: "h:MM TT",
  mediumTime: "h:MM:ss TT",
  longTime: "h:MM:ss TT Z",
  isoDate: "yyyy-mm-dd",
  isoTime: "HH:MM:ss",
  isoDateTime: "yyyy-mm-dd'T'HH:MM:ss",
  isoUtcDateTime: "UTC:yyyy-mm-dd'T'HH:MM:ss'Z'",
};

// Internationalization strings
dateFormat.i18n = {
  dayNames: [
    "Sun",
    "Mon",
    "Tue",
    "Wed",
    "Thu",
    "Fri",
    "Sat",
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ],
  monthNames: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
};

// For convenience...
Date.prototype.format = function (mask, utc) {
  return dateFormat(this, mask, utc);
};

/**
 * 
 * @param {*} selector 
 */
const SharePointNavigatorLoadBreadCrumbs = (selector) => {
  try {
    const folderSelector = $(selector).val();
    start(`SharePointNavigatorLoadBreadCrumbs`);
    traceLog("SharePointNavigatorLoadBreadCrumbs", `for ${folderSelector}`);
    const breadcrumbFolders = folderSelector.split("/");
    let parentFolder = "";
    $("#breadcrumbs > text").remove();
    let depth = 0;

    const svgns = "http://www.w3.org/2000/svg";
    let text = document.createElementNS(svgns, "text");

    breadcrumbFolders.forEach((bcf, index) => {
      if (bcf.length > 0) {
        const id = `subFolder-${index}`;
        let tspan = document.createElementNS(svgns, "tspan");
        parentFolder = parentFolder.length > 0 ? `${parentFolder}/${bcf}` : bcf;

        tspan.setAttribute("class", "breadcrumb");
        tspan.setAttribute("path", parentFolder);
        tspan.setAttribute("id", id);
        tspan.setAttribute("dx", 1);
        tspan.textContent = `${bcf} ${index == breadcrumbFolders.length - 1 ? "" : ">"}`;

        text.append(tspan);
      }
    });
    $("#breadcrumbs").append(text);
    $("#breadcrumbs > text > tspan").each(function (index, element) {
      const id = $(this).attr("id");
      SharePointNavigatorRegisterLinkCallbacks(selector, `#${id}`, "breadcrumb");
    });
  } catch (error) {
    errorLog(error.message);
  } finally {
    end("SharePointNavigatorLoadBreadCrumbs");
  }
}; //LoadBreadCrumbs

const _folderSvgSelector =  "div[id='folders'] > svg";

/**
 *
 * @param {*} folderName
 * @param {*} absolutePath
 */
const SharePointNavigatorAddFolders = (selector, folderName = null, absolutePath = null) => {
  // variable for the namespace
  const svgns = "http://www.w3.org/2000/svg";

  let g = document.createElementNS(svgns, "g");
  g.setAttribute("id", `folder_${_nextFolder}`);

  let use = document.createElementNS(svgns, "use");
  //use.setAttribute("y", _nextFolder);
  use.setAttribute("transform", `translate(0 ${_nextFolder})`);
  use.setAttribute("href", "#folderSvg");

  g.append(use);

  //font-family="Arial" font-size="16"
  let text = document.createElementNS(svgns, "text");
  text.textContent = folderName ? folderName : `folder ${_nextFolder}`;
  const id = crypto.randomUUID();
  text.setAttribute("id", id);
  text.setAttribute("transform", `translate(${_increment} ${_nextFolder + 15})`);
  text.setAttribute("class", "folder-name");
  text.setAttribute("path", absolutePath);

  g.append(text);

  traceLog("AddFolders", text.textContent);
  $(_folderSvgSelector).append(g);
  _nextFolder = _nextFolder + _increment;

  SharePointNavigatorRegisterLinkCallbacks(selector, `#${id}`, 'folder-name');
}; // AddFolders

/*
 * SharePoint folder selector
 */
const SharePointNavigatorLoadFolders = async (selector, accessToken, folder) => {
  SharePointNavigatorResetFolders();
  start("SharePointNavigatorLoadFolders");
  const server = await GetSharepointSite();
  //const folders = await GetFolder(server, folder, accessToken);
  const children = await ListFolders(
    server,
    folder,
    accessToken,
    "?$select=Name,ServerRelativeUrl,Length,TimeLastModified&orderBy=Name asc"
  );
  const regex = /(.+)(\/)([\w]+)/;

  SharePointNavigatorLoadBreadCrumbs(selector);

  if (children?.d?.results?.length == 0) {
    SharePointNavigatorEmptyFolder(folder);
  }
  //
  // Create the folders
  //
  children.d.results.forEach((element, index) => {
    try {
      const leafFolder = element.ServerRelativeUrl.replace(regex, "$3");
      const matcher = /\/?sites\/[^\/]*\/(.*)/g;
      let absolutePath = element.ServerRelativeUrl;
      const iterator = absolutePath.matchAll(matcher);

      iterator.forEach((element) => {
        if (element[1].length > 0) {
          absolutePath = element[1];
          traceLog("SharePointNavigatorLoadFolders", `${leafFolder} ${absolutePath}`);
        }
      });
      traceLog("SharePointNavigatorLoadFolders", `[${index}] ${leafFolder}`);
      SharePointNavigatorAddFolders(selector, leafFolder, absolutePath);
    } catch (error) {
      errorLog(error.message);
    }
  });
  end('SharePointNavigatorLoadFolders');
}; // SharePointNavigatorLoadFolders

let _nextFolder = 0;
const _increment = 25;
const _offset = 25;

/**
 *
 * @param {*} folder
 */
const SharePointNavigatorEmptyFolder = (folder) => {
  try {
    traceLog("SharePointNavigatorEmptyFolder", `${folder}`);
    //alert(`${folder} is empty`);
    const svgns = "http://www.w3.org/2000/svg";
    let g = document.createElementNS(svgns, "g");
    g.setAttribute("id", "warn");

    let use = document.createElementNS(svgns, "use");
    use.setAttribute("transform", "translate(0 10) scale(0.02)");
    use.setAttribute("href", "#empty");
    g.append(use);

    let errorText = document.createElementNS(svgns, "text");
    errorText.setAttribute("class", "folder-warn");
    errorText.setAttribute("transform", "translate(20 20)");
    errorText.textContent = `No subfolders`;
    g.append(errorText);
    $(_folderSvgSelector).append(g);
  } catch (error) {
    errorLog(error.message);
  }
}; // EmptyFolder

/**
 */
const SharePointNavigatorResetFolders = () => {
  _nextFolder = 0;
  $("g[id^='folder_']").remove();
  $("#warn").remove();
}; // SharePointNavigatorResetFolders

/**
 * 
 * @param {*} parentSelector 
 * @param {*} selector 
 * @param {*} cssClass 
 */
const SharePointNavigatorRegisterLinkCallbacks = (parentSelector, selector, cssClass) => {
  traceLog("SharePointNavigatorRegisterLinkCallbacks", `register ${cssClass} callback for ${selector}`);
  $(selector)
    .on("mouseenter", function () {
      traceLog("SharePointNavigatorRegisterLinkCallbacks", `mouseenter ${selector}`);
      $(this).removeClass(cssClass);
      $(this).addClass(`${cssClass}-rollover`);
    })
    .on("mouseleave", function () {
      traceLog("SharePointNavigatorRegisterLinkCallbacks", `mouseleave ${selector}`);
      $(this).removeClass(`${cssClass}-rollover`);
      $(this).addClass(cssClass);
    });

  $(selector).on("click", async function () {
    try {
      traceLog("SharePointNavigatorRegisterLinkCallbacks", `click ${selector}`);
      const parent = $("#folder").val();
      const newParent = $(this).attr("path");
      const accessToken = await GetAccessToken();
      traceLog('SharePointNavigatorRegisterLinkCallbacks', `Set ${parentSelector} to ${newParent}`);
      $(parentSelector).val(newParent);
      $(parentSelector).trigger('change');
      await SharePointNavigatorLoadFolders(parentSelector, accessToken, newParent);
    } catch (error) {
      errorLog(error.message);
    }
  });
}; //SharePointNavigatorRegisterLinkCallbacks
